import type { Dictionary } from "./types";

export const en: Dictionary = {
	price_openShopping: "Open shopping list",
	price_priceBasis: "Price basis",
	price_liveUnavailable: "Shop prices could not be fetched. The estimated material list is shown below.",
	price_budgetTitle: "Your estimated build budget",
	price_budgetBreakdown: "How the budget is calculated",
	price_budgetRounding: "Budget rounding",
	price_missingPrice: "Price missing",
	price_missingCount: "{count} material rows have no price and are excluded from the material total.",
	price_shopQuote: "Shop quote",
	price_shopQuoteNote: "Compare the shop contents with the material list below before ordering.",
	price_materialDetails: "Materials and estimated prices",
	instr_phaseSupplies: "Materials and cuts for this work phase",
	instr_supplyCounts: "{materials} material rows · {cuts} cut rows",
	instr_phaseSuppliesNote: "This is the full work phase list. A phase may cover several build steps. Follow the current step instructions to see what to assemble now.",
	instr_openCutlist: "Open full cut list",
	mat_search: "Search materials or measurements",
	mat_searchResults: "{visible} of {total} material rows",
	mat_searchBudgetNote: "Budget and transport weight cover the full project.",
	mat_noResults: "No materials match your search.",
	cut_openBuild: "Open build guide",
	cut_localPreferences: "Stock lengths are saved for this project on this device",
	cut_savedTooShort: "{dimension} mm: the saved {length} mm stock length is too short for the updated cuts. Auto is used until you choose a longer length.",
	cut_search: "Search parts or measurements",
	cut_noResults: "No parts match your search.",
	cut_purchaseOverview: "{count} stock boards · {dimensions} dimensions",
	cut_purchaseSummary: "Purchase quantities from the cut plan",
	cut_purchaseNote: "This covers the full cut plan. Parts needing separate planning are listed below.",
	cut_purchaseBoards: "{count} boards × {length} mm",
	cut_stockTooShort: "Too short for the longest part",
	cut_clearSearch: "Clear search",

	/* ── App-wide ────────────────────────────────────────── */
	app_name: "Sauna Creator",

	/* ── Navigation ──────────────────────────────────────── */
	nav_home: "Home",
	nav_dashboard: "My Projects",
	nav_signIn: "Sign in",
	nav_signOut: "Sign out",

	/* ── Landing page ────────────────────────────────────── */
	landing_title: "Build the sauna in the forest before you build it for real.",
	landing_subtitle:
		"Draw your outdoor sauna in 3D, see it in evening light and take the material list to the timber yard.",
	landing_feature3d: "Live 3D model",
	landing_featureMaterials: "Auto-generated material takeoff",
	landing_featureCutlist: "Cut list with exact lengths",
	landing_featureRules: "Swedish baseline assumptions",
	landing_primaryCta: "Start designing",
	landing_goToProjects: "Go to my projects →",
	landing_typeSquare: "Rectangular",
	landing_typeBarrel: "Barrel sauna",
	landing_typeTwoRoom: "With lounge",
	landing_ctaNote: "Free. No account needed to start.",
	landing_typesTitle: "Pick a starting shape",
	landing_typeSquareDesc: "One room",
	landing_typeTwoRoomDesc: "Hot room and lounge under one roof",
	landing_filmPlanTitle: "{size} mm. Hot room, lounge and a view of the lake.",
	landing_filmPlanBody: "This is {preset}, one of three layouts for the two-room sauna. Every measurement, door and window can change.",
	landing_filmListsTitle: "{rows} rows to buy. {cuts} rows on the cut list.",
	landing_filmListsBody: "Every stud, board and box of screws in the model ends up on the lists, with length and quantity.",
	landing_filmPriceTitle: "About {price} in materials.",
	landing_filmPriceBody: "The price updates when you change size, panelling or heater.",
	landing_filmPriceCta: "Draw your own sauna",
	landing_stepsTitle: "How it works",
	landing_step1Title: "Pick a shape",
	landing_step1Body: "One room, or two rooms with a lounge. Everything can change later.",
	landing_step2Title: "Pull the walls",
	landing_step2Body: "Size, doors, windows and benches change right in the 3D model.",
	landing_step3Title: "Take the lists",
	landing_step3Body: "Shopping list, cut list and build guide, with heater, ventilation and clearances checked.",
	landing_outputsTitle: "What you get",
	landing_output3dDesc: "Rotate and section the model to see the frame, panelling and benches before anything is bought.",
	landing_outputMaterialsDesc: "Timber, insulation, panelling, screws and fittings in quantities you can shop from, with an estimated cost.",
	landing_outputCutlistDesc: "A length for every piece, optimised against stock lengths to keep waste low.",
	landing_outputRulesDesc: "Heater size, breaker, ventilation and clearances checked against Swedish baseline rules and manufacturer tables.",
	landing_referenceDesignsTitle: "Four designs to start from",
	landing_referenceDesignsIntro:
		"Compare a small wood-fired model, an electric four-person sauna, and two larger two-room layouts. Each proposal has its own plan, material takeoff, and budget.",

	/* ── Auth page ───────────────────────────────────────── */
	auth_title: "Welcome back",
	auth_subtitle: "Sign in to manage your sauna projects",
	auth_emailLabel: "Email",
	auth_emailPlaceholder: "you@email.com",
	auth_passwordLabel: "Password",
	auth_passwordPlaceholder: "Enter password",
	auth_signInButton: "Sign in",
	auth_signUpButton: "Create account",
	auth_switchToSignUp: "Don't have an account? Create one",
	auth_switchToSignIn: "Already have an account? Sign in",
	auth_forgotPassword: "Forgot password?",
	auth_errorGeneric: "Something went wrong. Please try again.",
	auth_titleRegister: "Create account",
	auth_subtitleRegister: "Create a free account to start designing.",
	auth_registerSuccess:
		"Account created! Check your email to confirm, or sign in directly.",
	auth_submitting: "Please wait...",
	auth_switchToSignUpLink: "Create account",
	auth_switchToSignInLink: "Sign in",
	auth_passwordShow: "Show password",
	auth_passwordHide: "Hide password",

	/* ── Dashboard ───────────────────────────────────────── */
	dashboard_title: "My Projects",
	dashboard_empty: "You don't have any saved projects yet.",
	dashboard_newDesign: "New design",
	dashboard_compare: "Compare",
	dashboard_delete: "Delete",
	dashboard_deleteConfirm: "Are you sure you want to delete this project?",
	dashboard_deleteCancel: "Cancel",
	dashboard_loadError: "Could not load your projects.",
	admin_deniedTitle: "Access denied",
	admin_deniedBody: "Only superusers can open the admin panel.",
	dashboard_welcome: "Here are your saved sauna designs.",
	dashboard_newProject: "+ New Project",
	dashboard_loading: "Loading projects…",
	dashboard_emptyTitle: "No projects yet",
	dashboard_emptyBody:
		"Create your first project and start designing your dream sauna with live 3D and precise material takeoffs.",
	dashboard_emptyAction: "Create your first project →",
	dashboard_compareEyebrow: "Comparison Advisor",
	dashboard_compareTitle: "Compare up to four projects before you decide",
	dashboard_compareSelected: "selected",
	dashboard_compareAction: "Compare selected",
	dashboard_compareHelper0:
		"Create your first project. Once you have at least two saved projects the advisor can compare them.",
	dashboard_compareHelper1:
		"Create or duplicate at least one more project to let the advisor weigh the alternatives.",
	dashboard_compareHelperNone:
		"Select 2–4 projects to get a recommendation on which is the smartest to build.",
	dashboard_compareHelperOne:
		"Select at least one more project so the advisor can weigh the alternatives.",
	dashboard_compareHelperMax:
		"Select at most four projects at a time to keep the comparison clear.",
	dashboard_compareHelperReady:
		"Ready to compare. The advisor weighs cost, running expenses, buildability, and regulatory risk.",
	dashboard_typeSquare: "Rectangular",
	dashboard_typeBarrel: "Barrel sauna",
	dashboard_typeTwoRoom: "With lounge",
	dashboard_typeIndoor: "Indoor",
	dashboard_ariaCompare: "Compare",
	dashboard_ariaConfirmDelete: "Confirm: delete",
	dashboard_cardDelete: "Delete",
	dashboard_cardCancel: "Cancel",
	dashboard_cardLastModified: "Last modified:",
	dashboard_cardOpen: "Open →",
	dashboard_defaultName: "New Sauna",

	/* ── Wizard ──────────────────────────────────────────── */
	wizard_stepName: "Name your project",
	wizard_stepShape: "Choose shape",
	wizard_stepSize: "Set dimensions",
	wizard_stepLocation: "Location & climate",
	wizard_next: "Next",
	wizard_back: "Back",
	wizard_start: "Start designing",
	wizard_stepOf: "Step {step} of {total}",
	wizard_step1Title: "What type of sauna do you want to build?",
	wizard_step1Desc: "Choose the shape that best fits your site and style.",
	wizard_step1Aria: "Choose sauna type",
	wizard_step2Title: "How many people at a time?",
	wizard_step2Desc:
		"We suggest a size based on capacity. You can adjust the exact dimensions in the next step.",
	wizard_step2Aria: "Choose capacity",
	wizard_step2SuggestedSize: "Suggested size",
	wizard_step3Title: "How do you want to heat the sauna?",
	wizard_step3Desc:
		"The choice affects installation, running cost, and experience.",
	wizard_step3Aria: "Choose heating",
	wizard_step4Title: "Where will you build it?",
	wizard_step4Desc:
		"Choose the closest climate zone for preliminary structural sizing. You can refine this later with local guidance.",
	wizard_step4Hint: "Optional for the Swedish market profile.",
	wizard_step4Placeholder: "e.g. 981 34",
	wizard_step4EstimatedTime: "Estimated build time:",
	wizard_step4About: "about",
	wizard_step4Hours: "hours",
	wizard_step4Weekends: "weekends",
	wizard_launchCta: "Open planner",
	wizard_typeSquare: "Rectangular",
	wizard_typeSquareDesc: "Traditional cabin — easy to build and extend",
	wizard_typeIndoorDesc: "A sauna inside an existing room – basement, bathroom or storeroom",
	wizard_typeIndoor: "Indoor",
	wizard_typeBarrel: "Barrel sauna",
	wizard_typeBarrelDesc: "Round, compact barrel shape",
	wizard_typeTwoRoom: "With lounge",
	wizard_typeTwoRoomDesc: "Sauna + changing room in one",
	wizard_capacity2: "2 people",
	wizard_capacity2Desc: "Compact and quick to heat",
	wizard_capacity4: "4 people",
	wizard_capacity4Desc: "Most popular — perfect for the family",
	wizard_capacity6: "6 people",
	wizard_capacity6Desc: "Spacious — for the crew",
	wizard_heaterWood: "Wood-burning stove",
	wizard_heaterWoodDesc:
		"Authentic experience. Requires chimney and inspection.",
	wizard_heaterElectric: "Electric heater",
	wizard_heaterElectricDesc:
		"Easier installation. Requires 3-phase power (above 4.5 kW).",
	wizard_permitOk: "No building permit required",
	wizard_permitWarning: "Building notification required",
	wizard_permitError: "Full building permit required",

	/* ── Builder tabs ────────────────────────────────────── */
	builder_tabStructure: "Structure",
	builder_tabMaterials: "Materials",
	builder_tabRules: "Safety & planning",
	builder_tabExport: "Export",
	builder_tabBuild: "Build plan",

	/* ── Rules / safety ──────────────────────────────────── */
	rules_tabLabel: "Safety & planning",
	rules_disclaimerTitle: "Checked against Swedish rules — confirm yours locally",
	rules_disclaimerBody:
		"This planner uses Swedish baseline assumptions and best-practice guidance. Verify all structural, fire-safety, electrical, and permit details locally before building.",
	build_disclaimerTitle: "Planner-only build guide",
	build_disclaimerBody:
		"The build sequence below is preliminary planning output. It is not a permit-approved construction package and must be verified against local code, site, and climate conditions.",

	/* ── Location / climate ──────────────────────────────── */
	location_inputLabel: "Postal code",
	location_inputHint: "Optional for the Swedish market profile.",
	location_inputPlaceholder: "e.g. 981 34",
	climate_inputLabel: "Climate zone",
	climate_inputHint:
		"Choose the closest snow-load class for your site. You can refine this later with a local engineer.",
	location_summaryLabel: "Climate",
	location_summaryFallback: "Standard climate",

	/* ── Export / PDF ─────────────────────────────────────── */
	export_title: "Preliminary planning output",
	export_disclaimerTitle: "Swedish baseline assumptions",
	export_disclaimers: [
		"This PDF is for planning only and does not claim local code compliance.",
		"Verify all structural, fire-safety, electrical, and permit details with local professionals before building.",
		"Material assumptions, climate classes, and sequencing should be adjusted to your local market and site.",
	],
	export_footer: "Generated by Sauna Creator — saunacreator.com",
	export_downloadPdf: "Download PDF",
	export_downloadCsv: "Download CSV",

	/* ── Share ────────────────────────────────────────────── */
	share_title: "Share your design",
	share_subtitle: "Send this link to someone to show your sauna design.",
	share_copyLink: "Copy link",
	share_copied: "Copied!",
	share_openBuilder: "Open in builder",
	share_invalidLink: "Invalid share link",
	share_invalidLinkDesc: "The link appears to be broken or incomplete.",
	share_backToHome: "Back to home →",
	share_typeSquare: "Rectangular sauna",
	share_typeBarrel: "Barrel sauna",
	share_typeTwoRoom: "Sauna with lounge",
	share_typeIndoor: "Indoor sauna",
	share_materialCost: "Material cost",
	share_diyBudget: "DIY budget",
	share_marketPackage: "Market comparison",
	share_estimatedInclVat: "estimated incl. VAT",
	share_sessionCost: "Cost per session",
	share_heatUpTime: "min heat-up",
	share_buildTime: "Build time",
	share_permitStatus: "Permit status",
	share_permitOk: "Not required ✅",
	share_permitWarning: "Notification ⚠️",
	share_permitRequired: "Permit required 🚫",
	share_heatingLabel: "Heating",
	share_heaterWood: "🔥 Wood-burning stove",
	share_heaterElectric: "⚡ Electric heater",
	share_openingsLabel: "Openings",
	share_openingsNone: "None",
	share_doors: "door",
	share_windows: "window",
	share_postalCode: "Postal code",
	share_climateZone: "Climate zone",
	share_monthlyRunningCost: "Running cost / month",
	share_roofLabel: "Roof",
	share_foundationLabel: "Foundation",
	share_flatTerrain: "flat terrain",
	share_createOwn: "Create your own sauna",
	share_generatedBy: "Generated by",
	share_weekends: "weekends",
	share_viewOnly: "View-only preview",

	/* ── Shared design page (/d/:id SSR) ──────────────────── */
	design_openInBuilder: "Open in builder",
	design_viewOnly: "Shared preview",
	design_createOwn: "Create your own sauna",
	design_heaterLabel: "Heater",
	design_foundationLabel: "Foundation",
	design_buildTimeLabel: "Build time",
	design_permitLabel: "Permit",
	design_piers: "piers",
	design_beams: "beams",
	design_hours: "h",
	design_notFoundTitle: "Design not found",
	design_notFoundBody:
		"This sauna does not exist or has not been shared publicly.",
	design_backToHome: "Back to home",

	/* ── Comparison ──────────────────────────────────────── */
	comparison_title: "Compare designs",
	comparison_empty: "Select at least two designs to compare.",
	comparison_selectPrompt: "Select designs to compare",
	comparison_loading: "Loading comparison…",
	comparison_emptyTitle: "The comparison needs at least two saved projects",
	comparison_emptyMissing:
		"selected project could not be found in your saved designs.",
	comparison_emptyBody:
		"Select at least two saved projects in the dashboard to get a recommendation.",
	comparison_backToDashboard: "Back to my projects",
	comparison_eyebrow: "Comparison Advisor",
	comparison_recommendedSuffix: "is the safest choice right now",
	comparison_advisorBody:
		"The advisor weighs material cost, running expenses, regulatory risk, build effort and practical logistics in the same decision layer.",
	comparison_back: "Back",
	comparison_recommended: "Recommended solution",
	comparison_overallScore: "Overall score",
	comparison_overallScoreBody:
		"Highest overall balance between economy, risk, and buildability.",
	comparison_permitStatus: "Permit status",
	comparison_permitOk: "Lowest regulatory friction in the selection.",
	comparison_permitWarning:
		"Requires more preparation than a simple permit-exempt structure.",
	comparison_whyWins: "Why this one wins",
	comparison_watchOut: "Things to watch before deciding",
	comparison_sideBySide: "Compare side by side",
	comparison_measure: "Measure",
	comparison_best: "Best",
	comparison_totalEconomy: "Total economy",
	comparison_approvalRisk: "Approval risk",
	comparison_buildComplexity: "Buildability",
	comparison_practicality: "Practicality",
	comparison_type: "Type",
	comparison_size: "Size",
	comparison_heating: "Heating",
	comparison_materialCost: "Material cost",
	comparison_diyBudget: "DIY budget",
	comparison_marketPackage: "Market comparison",
	comparison_monthlyCost: "Running / month",
	comparison_permit: "Permit status",
	comparison_buildTimeline: "Build time",
	comparison_proSteps: "Professional steps",
	comparison_transport: "Transport",
	comparison_transportNotCalc: "Not calculated",

	/* ── Builder Tabs ─────────────────────────────────────── */
	builder_tabMaterialLabel: "Materials",
	builder_tabCutlistLabel: "Cut list",
	builder_tabPriceLabel: "Price",
	builder_tabFoundationLabel: "Foundation",
	builder_tabInstructionsLabel: "Instructions",
	builder_tabBBRLabel: "Building code",
	builder_fabDownloadPdf: "Download PDF",
	builder_fabRenderPhoto: "Render image",
	builder_fabRendering: "⏳ Rendering...",
	builder_renderTitle: "Photo-realistic render",
	builder_renderAlt: "Rendered sauna",
	builder_renderDownload: "Download",
	builder_barrel_deprecated_title:
		"Barrel sauna is no longer active for new designs",
	builder_barrel_deprecated_body:
		"You can keep editing this saved barrel design, but the engine is no longer maintained. We recommend migrating new projects to TwoRoom Panorama.",
	builder_barrel_deprecated_cta: "Migrate to Lakeside",
	builder_barrel_saveDeprecatedTitle: "Save barrel sauna?",
	builder_barrel_saveDeprecatedBody:
		"Barrel is no longer active — continue at your own risk or migrate to TwoRoom Panorama.",
	builder_barrel_saveContinue: "Continue saving",
	builder_barrel_saveMigrate: "Migrate to Lakeside",

	/* ── Step Bar ─────────────────────────────────────────── */
	step_designLabel: "Design",
	step_designSummary: "Dimensions, openings and heating",
	step_materialLabel: "Materials",
	step_materialSummary: "Quantities, cut list and price",
	step_buildLabel: "Build",
	step_buildSummary: "Instructions, rules and PDF",
	step_statusDone: "Done",
	step_statusActive: "Active",
	step_statusNext: "Next",

	/* ── Dimension Panel ──────────────────────────────────── */
	dim_panelLabel: "Design panel",
	dim_groupSauna: "Sauna",
	dim_groupAppearance: "Appearance",
	dim_groupConditions: "Site & heat",
	vis_layers: "Layers",
	vis_presetsLabel: "Views",
	vis_presetFinished: "Finished",
	vis_presetFraming: "Framing",
	vis_presetSafety: "Safety zones",
	camera_exterior: "Exterior",
	instr_assemblyPiers: "1 · Piers",
	instr_assemblyBrackets: "2 · Brackets",
	instr_assemblyBeams: "3 · Beams",
	instr_assemblyComplete: "Piers and beams in place",
	instr_assemblyPlay: "Show assembly",
	instr_assemblyPause: "Pause",
	instr_assemblyReplay: "Replay",
	instr_assemblyProgress: "Assembly progress",
	instr_assemblyHint: "Schematic: piers, brackets and beams. Follow the dimensions and fixings in the step instructions.",
	film_title: "Build animation",
	film_play: "Play",
	film_pause: "Pause",
	film_prevStep: "Previous step",
	film_nextStep: "Next step",
	film_stepOf: "Scene {current} of {total}",
	film_stepProgress: "Step progress",
	film_kitLabel: "Parts and tools for this step",
	film_planLabel: "Plan",
	film_scopeGuideStep: "This stage",
	film_scopeWholeBuild: "Whole build",
	film_hint: "Orange parts go on in this step; grey is already built. Counts come from your design.",
	film_stepPlinths: "Set and level the piers",
	film_stepPostShoes: "Fit the post brackets",
	film_stepBeams: "Lay the beams",
	film_stepFloorJoists: "Lay the floor joists",
	film_stepFloorInsulation: "Fit the subfloor and insulate the floor",
	film_stepWallBack: "Raise the back wall",
	film_stepWallSides: "Raise the side walls",
	film_stepWallFront: "Raise the front wall",
	film_stepRoofJoists: "Set the rafters",
	film_stepRoofDeck: "Deck and cover the roof",
	film_stepWindBarrier: "Fit the wind barrier",
	film_stepFurring: "Fix the battens for the air gap",
	film_stepCladding: "Clad the walls",
	film_stepDoorsWindows: "Fit the door and windows",
	film_stepWallInsulation: "Insulate walls and ceiling",
	film_stepVaporBarrier: "Fit the foil vapour barrier and battens",
	film_stepInnerPanel: "Panel the interior",
	film_stepBenches: "Build the benches",
	film_stepHeater: "Fit the heater, heat shields and vents",
	film_stepChimney: "Install the chimney",
	film_stepPartition: "Raise the partition wall",
	film_stepPartitionPanel: "Panel the partition and hang its door",
	film_stepFinish: "Steps and final details",
	film_partPlinth: "Concrete pier",
	film_partPostShoe: "Post bracket",
	film_partBeam: "Beam",
	film_partFloorJoist: "Floor joist",
	film_partSubfloorLedger: "Subfloor ledger",
	film_partSubfloor: "Subfloor board",
	film_partInsulation: "Insulation",
	film_partStud: "Stud",
	film_partPlate: "Sole and top plate",
	film_partRoofJoist: "Rafter",
	film_partRoofDeck: "Roof decking",
	film_partRoofCovering: "Roof covering",
	film_partWindBarrier: "Wind barrier",
	film_partFurring: "Batten",
	film_partCladding: "Cladding",
	film_partCornerBoard: "Corner board",
	film_partDoor: "Door",
	film_partVaporBarrier: "Foil vapour barrier",
	film_partBatten: "Batten",
	film_partSaunaPanel: "Sauna panel",
	film_partBenchFrame: "Bench frame",
	film_partBenchSlat: "Bench slat",
	film_partHeater: "Heater",
	film_partHeatShield: "Heat shield",
	film_partChimney: "Chimney",
	film_partEntryStep: "Step",
	dim_panelTitle: "Your sauna",
	dim_sectionDimensions: "Dimensions",
	dim_sectionOpenings: "Openings",
	dim_sectionDeck: "Deck",
	dim_sectionRoof: "Roof",
	dim_sectionSite: "Site & foundation",
	dim_sectionThermal: "Thermal",
	dim_partitionSplit: "Partition wall",
	dim_wallHeightLabel: "Wall height",
	indoor_roomTitle: "The room the sauna stands in",
	indoor_roomSummaryFree: "Free-standing in the room",
	indoor_roomSummaryWalls: "Against existing walls: {count}",
	indoor_existingWallsLabel: "Against an existing wall",
	indoor_existingWallsHint: "A wall against the room's own wall gets a 20 mm air gap and a wood-fibre board instead of outer panelling. Free-standing walls get panelling straight on the studs.",
	indoor_houseAboveLabel: "House above the sauna ceiling",
	indoor_houseAboveHint: "From the sauna ceiling to the ridge of the house. Sets how long the chimney through the house is.",
	indoor_houseAboveUnset: "{m} m assumed",
	twoRoom_sectionLayout: "Layout",
	twoRoom_sectionLabel: "Two-room layout",
	twoRoom_sectionIntro:
		"Choose a ready layout or open advanced for exact controls.",
	twoRoom_presetGroupAria: "Choose layout preset",
	twoRoom_presetClassicTitle: "Classic",
	twoRoom_presetClassicDescription:
		"Short-side entry, standard glazing, and lounge.",
	twoRoom_presetClassicAria: "Choose classic layout",
	twoRoom_presetLakesideTitle: "Lakeside",
	twoRoom_presetLakesideDescription:
		"Long-side entry and panorama glazing with a lounge.",
	twoRoom_presetLakesideAria: "Choose lakeside panorama layout",
	twoRoom_presetOpenTitle: "Open Panorama",
	twoRoom_presetOpenDescription:
		"One open sauna with long-side entry and panorama glazing.",
	twoRoom_presetOpenAria: "Choose open panorama without lounge",
	twoRoom_advancedToggle: "Advanced",
	twoRoom_entryWallLabel: "Entry wall",
	twoRoom_entryWallShort: "Short side",
	twoRoom_entryWallLong: "Long side",
	twoRoom_glazingStyleLabel: "Window style",
	twoRoom_glazingStandard: "Standard",
	twoRoom_glazingPanorama: "Panorama",
	twoRoom_saunaPositionLabel: "Sauna position",
	twoRoom_saunaPositionLeft: "Left",
	twoRoom_saunaPositionRight: "Right",
	twoRoom_relaxRoomLabel: "Lounge room",
	twoRoom_relaxToggleAria: "Toggle lounge room",
	twoRoom_relaxRoomOnSummary: "Lounge",
	twoRoom_relaxRoomOffSummary: "1-room",
	dim_openingSummaryNone: "No openings added",
	dim_openingDoorSingular: "door",
	dim_openingDoorPlural: "doors",
	dim_openingWindow: "window",
	dim_openingWindowPlural: "windows",
	dim_heaterWood: "Wood-burning stove",
	dim_heaterElectric: "Electric heater",
	dim_terrainFlat: "Flat terrain",

	/* ── Dimension Sliders ────────────────────────────────── */
	slider_sectionLabel: "Dimensions",
	units_label: "Units",
	units_metric: "Metric",
	units_imperial: "Imperial",
	units_ariaLabel: "Choose measurement units",
	slider_reset: "Reset",
	slider_areaLabel: "Area (m²)",
	slider_areaAriaLabel: "Floor area (square metres)",
	slider_unlockArea: "Unlock area",
	slider_lockArea: "Lock area",
	slider_lengthLabel: "Length",
	slider_lengthAriaLabel: "Length: {value} millimetres",
	slider_widthLabel: "Width",
	slider_widthAriaLabel: "Width: {value} millimetres",
	slider_postalCodeLabel: "Postal code",
	slider_postalCodePlaceholder: "e.g. 981 34",
	slider_climateZoneLabel: "Climate zone",
	slider_clampMax: "Max {value} — adjusted to the limit.",
	slider_clampMin: "Min {value} — adjusted to the limit.",
	slider_clampSeeRules: "See rules",

	/* ── Heater Calculator ────────────────────────────────── */
	heaterCalc_metaTitle:
		"Sauna Heater kW Calculator – right-size in seconds 2026 | Sauna Creator",
	heaterCalc_metaDescription:
		"Work out exactly how many kW your sauna heater needs. Accounts for volume, glass area and exposed stone. Free online — open directly as a full sauna plan.",
	heaterCalc_breadcrumbCalculators: "Calculators",
	heaterCalc_eyebrow: "FREE CALCULATOR",
	heaterCalc_title: "Sauna Heater kW Calculator",
	heaterCalc_lead:
		"Get a correctly sized heater in under a minute. Adjust dimensions, glass and stone — we work out effective volume, fuse rating and recommended power.",
	heaterCalc_formLabel: "Inputs",
	heaterCalc_inputsHeading: "Dimensions and materials",
	heaterCalc_widthLabel: "Width (interior)",
	heaterCalc_lengthLabel: "Length (interior)",
	heaterCalc_glassLabel: "Glass area",
	heaterCalc_glassHelp:
		"Add up all glass doors and windows in the sauna. Glass loses much more heat than panelling, so each m² of glass counts as 1.2 m³ of extra room volume — the allowance heater manufacturers give.",
	heaterCalc_stoneLabel: "Exposed stone area",
	heaterCalc_stoneHelp:
		"Only counts untreated stone surfaces on walls (e.g. tile walls). Does not include the sauna stones in the heater itself.",
	heaterCalc_heaterTypeLabel: "Heater type",
	heaterCalc_heaterElectric: "Electric",
	heaterCalc_heaterWood: "Wood-fired",
	heaterCalc_resultEyebrow: "Recommended power",
	heaterCalc_resultVolume: "Effective volume",
	heaterCalc_resultFuse: "Fuse",
	heaterCalc_resultElectrical: "Electrical",
	heaterCalc_openInBuilder: "Open as a full sauna plan",
	heaterCalc_ctaHint: "Want to learn more about sizing?",
	heaterCalc_readGuide: "Read the full guide",
	heaterCalc_howItWorksHeading: "How the calculator works",
	heaterCalc_howItWorksBody:
		"Heater manufacturers rate every heater for a range of room volumes — Harvia's 9 kW KIP90E, for example, is rated for 8–14 m³. We work out the room volume from your dimensions (with a 2.1 m ceiling), add 1.2 m³ for each m² of glass or exposed stone, and pick the smallest standard heater (4.5 / 6 / 8 / 9 / 10.5 / 12 / 15 kW) whose rated range covers it.",
	heaterCalc_faqHowMuchKwQuestion: "How many kW does my sauna heater need?",
	heaterCalc_faqHowMuchKwAnswer:
		"Go by the room-volume range the manufacturer states for each heater, not a flat kW per m³. A 2.5 × 2.5 m sauna with a 2.1 m ceiling is about 13 m³, which a 9 kW heater (rated 8–14 m³) covers. Add 1.2 m³ per m² of glass door or exposed stone, and count uninsulated log walls as 1.5 times the volume.",
	heaterCalc_faqGlassPenaltyQuestion:
		"Why does glass increase the power needed?",
	heaterCalc_faqGlassPenaltyAnswer:
		"Glass has a U-value around 1.1–1.4 W/m²K, versus around 0.2 for an insulated sauna wall, so a glass door loses 5–7 times as much heat as the same area of timber. Heater manufacturers account for it by counting each m² of glass as 1.2 m³ of extra room volume, and so do we.",
	heaterCalc_faqFuseQuestion: "What fuse rating is needed?",
	heaterCalc_faqFuseAnswer:
		"At 400 V three-phase, a 4.5–6 kW heater needs a 10 A fuse, 8–10.5 kW needs 16 A, 12 kW needs 20 A and 15 kW needs 25 A. The installation must always be done by a licensed electrician.",

	/* ── Snow Zone Calculator ─────────────────────────────── */
	snowCalc_metaTitle:
		"Snow Zone Calculator – roof joist sizing for Swedish postal codes | Sauna Creator",
	snowCalc_metaDescription:
		"Look up the snow zone and recommended C24 roof joist for any Swedish postal code. Free calculator for outdoor saunas and permit-exempt outbuildings.",
	snowCalc_title: "Snow Zone & Joist Calculator",
	snowCalc_lead:
		"Look up the snow zone for your Swedish postal code per Boverket / EKS and get a recommended C24 joist for your sauna roof, based on Svenskt Trä's span tables.",
	snowCalc_inputsHeading: "Where are you building?",
	snowCalc_postalCodeLabel: "Postal code",
	snowCalc_postalCodePlaceholder: "e.g. 981 34",
	snowCalc_postalCodeHelp:
		"We map the first 2–3 digits to Boverket's snow-zone map (1.0–4.5). Leave empty to use default zone 2.0 (Stockholm).",
	snowCalc_spanLabel: "Roof span (m)",
	snowCalc_spanHelp:
		"Distance between load-bearing walls (typically the interior width for square saunas).",
	snowCalc_resultEyebrow: "Snow zone",
	snowCalc_resultRegion: "Region",
	snowCalc_resultRegionFallback: "—",
	snowCalc_resultJoist: "Recommended joist",
	snowCalc_resultSpacing: "c/c spacing",
	snowCalc_readGuide: "Read the snow-zone guide",
	snowCalc_howItWorksHeading: "How the sizing works",
	snowCalc_howItWorksBody:
		"The snow zone is the characteristic snow load on the ground (s_k) in kN/m². It combines with the roof span and on-centre spacing to select a C24 joist from Svenskt Trä's span tables. We round up to the next standard size (45×145 / ×170 / ×195 / ×220).",
	snowCalc_faqWhatIsZoneQuestion: "What is a snow zone?",
	snowCalc_faqWhatIsZoneAnswer:
		"A geographical value from 1.0 to 5.5 kN/m² indicating the characteristic snow load on the ground. Boverket publishes the map in EKS 11. Southern Sweden sits at 1.0–1.5, Stockholm around 2.0, the mountain regions up to 4.5+.",
	snowCalc_faqJoistSizingQuestion: "How is the joist sized?",
	snowCalc_faqJoistSizingAnswer:
		"We use Svenskt Trä's span table for C24 lumber at 600 mm on-centre spacing. The joist's bending moment and deflection are checked against snow zone × snow-shape factor. The result is the smallest joist depth that satisfies the limits.",
	snowCalc_faqDefaultZoneQuestion: "What if the postal code isn't found?",
	snowCalc_faqDefaultZoneAnswer:
		"We fall back to zone 2.0 (Stockholm average). If you're building in the mountains, increase the zone manually — for very high loads you may need an engineer's certificate.",

	/* ── Running Cost Calculator ──────────────────────────── */
	runCalc_metaTitle:
		"Sauna Running Cost Calculator – per session & monthly 2026 | Sauna Creator",
	runCalc_metaDescription:
		"Work out the real running cost of your sauna per session and per month. Free calculator: electric or wood-fired, heat-up time, kWh per session, monthly cost.",
	runCalc_title: "Sauna Running Cost Calculator",
	runCalc_lead:
		"What does it actually cost to use your sauna? Adjust dimensions, heater type and electricity price — and get heat-up time, kWh per session, and monthly cost instantly.",
	runCalc_inputsHeading: "Sauna & usage",
	runCalc_priceLabel: "Electricity price",
	runCalc_priceHelp:
		"Includes generation, grid fee, energy tax, and VAT. Check your latest bill for an exact figure. Typical European range: 0.20–0.50 €/kWh.",
	runCalc_sessionsLabel: "Sessions per month",
	runCalc_sessionsHelp:
		"How often do you use the sauna? 8 sessions = twice a week, typical for a household sauna.",
	runCalc_perMonth: "mo",
	runCalc_resultEyebrow: "Cost per session",
	runCalc_resultPerMonth: "Monthly cost",
	runCalc_resultHeatUp: "Heat-up time",
	runCalc_resultKwh: "Energy use",
	runCalc_resultHeaterSize: "Recommended heater",
	runCalc_howItWorksHeading: "How the cost is calculated",
	runCalc_howItWorksBody:
		"For electric heaters we compute the energy to heat the air (volume × ΔT × ρ × c) plus a thermal-mass factor (~3×) for walls, benches, and stones. We divide by the heater's efficiency and add the maintenance load during the session. For wood-fired we use an empirical model based on ~15 kg of birch per session.",
	runCalc_faqHeatUpQuestion: "How long does it take to heat a sauna?",
	runCalc_faqHeatUpAnswer:
		"35–60 minutes for a correctly sized electric heater to reach 80 °C. An undersized heater never reaches temperature, an oversized one wastes energy. Wood-fired saunas take 30–60 min depending on size and chimney draw.",
	runCalc_faqElectricVsWoodQuestion:
		"Is wood-fired or electric cheaper to run?",
	runCalc_faqElectricVsWoodAnswer:
		"In the calculator's starting room, {size} m with a {kw} kW heater, an electric session uses about {kwh} kWh, which costs {electricCost} at {price} per kWh. A wood-fired session burns about 15 kg of birch, roughly {woodCost}. Electric is the cheaper of the two below about {breakEven} per kWh, and wood is cheaper above it. Enter your own room and electricity price above to see where you land. Wood also means handling fuel and having the chimney swept.",
	runCalc_faqInsulationQuestion: "How much does good insulation save?",
	runCalc_faqInsulationAnswer:
		"An uninsulated outdoor log-cabin uses about 30 % more energy than an insulated build. Glass doors and windows further increase losses — each m² of glass adds about 1.2 m³ of effective volume.",

	/* ── Builder Templates ────────────────────────────────── */
	templateGallery_title: "Common sizes to start from",
	templateGallery_subtitle:
		"Fills in the dimensions, door and heater for you. Drag any of it afterwards.",
	templateGallery_openLabel: "Common sizes",
	templateGallery_useTemplate: "Use these dimensions",
	templateGallery_floorArea: "{value} m² floor",
	templateGallery_heaterWood: "Wood-fired heater",
	templateGallery_heaterElectric: "Electric heater",

	/* ── Builder first-run intro / coach-marks ────────────── */
	builderIntro_entryTitle: "Let's set up your sauna",
	builderIntro_entrySubtitle:
		"You choose the dimensions and openings — we work out the frame, purchase list, cut list and price.",
	builderIntro_entryTemplate: "Start from a common size",
	builderIntro_entryTemplateHint:
		"Fills in the dimensions, door and heater for you — one click.",
	builderIntro_entryBlank: "Set the dimensions myself",
	builderIntro_entryBlankHint:
		"Start at the default dimensions and drag them where you want.",
	builderIntro_entrySoloStart: "Get started",
	builderIntro_skip: "Skip",
	builderIntro_next: "Next",
	builderIntro_done: "Got it",
	builderIntro_stepLabel: "Step {current} of {total}",
	builderIntro_canvasTitle: "Shape it in 3D",
	builderIntro_canvasBody:
		"Drag to rotate, scroll to zoom, and grab a door or window to move it right on the model.",
	builderIntro_tabsTitle: "Design → purchase list → cut list → price",
	builderIntro_tabsBody:
		"Design here, then step through the tabs for a shopping list, an optimised cut list, and a running price estimate.",
	template_squareBackyard_name: "Backyard sauna 2.5 × 2.0",
	template_squareBackyard_description:
		"Two people, a wood heater and the smallest footprint. The simplest way to get started.",
	template_squareFamily_name: "Family sauna 3.0 × 3.0",
	template_squareFamily_description:
		"Room for four, electric heater, glazed door and a side window. Includes a small front deck.",
	template_squareCouple_name: "Couple's sauna 2.0 × 2.0",
	template_squareCouple_description:
		"Two people, wood heater, the smallest possible footprint. Fits the tightest plot.",
	template_barrelClassic_name: "Classic barrel 2.0 × 2.3",
	template_barrelClassic_description:
		"Three to four people, wood heater with a small view port. Clean lines, quick to build.",
	template_twoRoomCabin_name: "Two-room sauna 3.5 × 2.9",
	template_twoRoomCabin_description:
		"Sauna plus a relaxation room with a side window, wood heater. Perfect for a cabin or lakeside plot.",
	template_twoRoomCompact_name: "Compact two-room 2.5 × 3.0",
	template_twoRoomCompact_description:
		"Sauna plus a small relaxation nook, wood heater. A narrow footprint for the smaller garden.",
	template_twoRoomLakeside_name: "Lakeside two-room 4.0 × 3.0",
	template_twoRoomLakeside_description:
		"Spacious relaxation room with a large panorama window, electric heater and a deck. Built for the view.",

	/* ── Help Hints ───────────────────────────────────────── */
	helpHint_ariaLabel: "Show help",
	helpHint_learnMore: "Learn more",
	helpHint_areaTitle: "Floor area",
	helpHint_areaBody:
		"Total area inside the sauna's exterior walls. Up to 30 m² is normally a permit-exempt outbuilding in Sweden (50 m² outside zoned areas) — larger requires a building permit.",
	helpHint_lengthTitle: "Length",
	helpHint_lengthBody:
		"Outer framing dimension along the long side. Width × length drives panel material and the cut list.",
	helpHint_wallHeightTitle: "Wall height",
	helpHint_wallHeightBody:
		"The benches follow the ceiling, so the heat stays where you sit. A lower wall means less air to heat and a smaller heater. A higher one gives headroom and space for bigger windows, but lifts the foot bench: from 2,250 mm it needs a step.",
	helpHint_widthTitle: "Width",
	helpHint_widthBody:
		"Outer framing dimension along the short side, the side the roof joists span. How wide it can go depends on the snow zone: beyond the span table the roof needs glulam beams. Narrow saunas fit one bench tier; wider lets you stack two.",
	helpHint_postalCodeTitle: "Postal code",
	helpHint_postalCodeBody:
		"We use the postal code to look up your snow zone and size roof joists per Boverket / EKS.",
	helpHint_climateZoneTitle: "Climate zone",
	helpHint_climateZoneBody:
		"Snow-load class drives roof joist size and on-centre spacing. When in doubt, pick the higher class.",

	/* ── Openings Editor ──────────────────────────────────── */
	openings_sectionLabel: "Openings",
	openings_intro: "Add a door directly or place a window in the 3D view.",
	openings_addDoor: "Door",
	openings_addWindow: "Window",
	openings_placingWindow: "Place window in 3D…",
	openings_emptyState:
		"No openings yet. Add a door directly or choose Window to place in the model.",
	openings_typeDoor: "Door",
	openings_typeWindow: "Window",
	openings_metaDoor: "Anchored at floor level",
	openings_metaWindow: "Placed freely on selected wall",
	openings_remove: "Remove",
	openings_wallLabel: "Wall",
	openings_wallFront: "Front",
	openings_wallBack: "Back",
	openings_wallLeft: "Left",
	openings_wallRight: "Right",
	openings_wallDivider: "Divider",
	openings_widthLabel: "Width (mm)",
	openings_heightLabel: "Height (mm)",
	openings_sizePresetLabel: "Standard size",
	openings_sizeCustom: "Custom size",
	openings_doorLeafLabel: "Door leaf",
	openings_doorLeafWood: "Wood",
	openings_doorLeafGlass: "Full glass",
	openings_hingeLabel: "Hinges (seen from outside)",
	openings_hingeLeft: "Left",
	openings_hingeRight: "Right",
	openings_swingLabel: "Opens",
	openings_swingOut: "Outward",
	openings_swingIn: "Inward",
	openings_swingInWarning:
		"A sauna door should open outward, so it can be pushed open if someone feels unwell.",
	openings_error_invalidSize: "Width and height must be greater than 0.",
	construction_roofReview: 'The roof load path, connections and moisture design require site-specific verification. Timber sizes are preliminary; the model is not an engineered truss drawing.',
	construction_referenceOpen: "View the roof proposal in 3D",
	construction_referenceScope: "Separate reference: 4 × 2.5 m, 8° mono-pitch roof. Shows the reinforcements and their cut list. Does not change your sauna or shopping list. Connections and the whole building remain under design.",
	construction_referencePanels: "Show roof panels",
	construction_referenceLoading: "Loading construction view…",
	construction_referenceControls: "Drag to rotate. Pinch or scroll to zoom.",
	construction_referenceParts: "cut parts",
	construction_referenceDownload: "Download reference cut list (CSV)",
	construction_basisTitle: "Construction basis for your outdoor sauna",
	construction_planningStatus: "Details outstanding",
	construction_basisIntro: "The following decisions and sources form a planning basis. Reference drawings apply to their own dimensions and loads; they do not approve this model.",
	construction_structureTitle: "1. Choose a complete roof system",
	construction_gableSystem: "A gable roof needs a documented truss or a structural ridge beam with supports carrying loads to the foundation. Two rafters meeting at the ridge are not a structural specification. Retain all members and connections in the chosen design. A vaulted interior needs a design for that arrangement. A truss supplier can usually provide documentation with the roof package.",
	construction_monoSystem: "For a mono-pitch roof, actual beam spans, bearings and anchorage must match the structural documentation. Check snow, wind and total roof weight. The displayed timber section is an estimate, not a verified beam calculation.",
	construction_connectionsTitle: "2. Specify the connections",
	construction_benchMountingTitle: "Benches: frame and wall supports",
	construction_benchMountingDetail: "Size the bench frame for the selected span, timber grade and load. Wall screws must pass through the cleat, panel and air gap into structural studs or blocking. Plan solid packing behind the supports. The bench screws in the material list are for the frame and seat boards; wall anchorage and retention against movement require a separate specification. End bearings and correct fit do not verify load capacity.",
	construction_connectionDetail: "Each structural joint needs a specified connector or connection, fasteners and installation pattern. Hole count is not a nailing schedule. Follow the table and installation drawing for the exact product, timber thickness and load direction. Include wall-to-foundation anchorage and wind bracing.",
	construction_moistureTitle: "3. Design moisture protection through the whole roof",
	construction_moistureDetail: "The starting concept is a ventilated outer roof with a wind barrier separating insulation from the air path. Provide working inlets and outlets protected from rain and pests. An empty gap alone is insufficient. Seal the warm side and all junctions; leaking sauna air must not ventilate the roof. The complete detail is still absent from this model.",
	construction_envelopeTitle: "4. Use a consistent sauna insulation system",
	construction_envelopeDetail: "The model shows mineral wool and sauna foil. Use products rated for sauna temperatures and follow the system instructions for tapes, seams and penetrations. Do not combine them with an indoor room-within-a-room guide based on a different wall assembly. Follow the chimney system clearances and sealing details at its penetration.",
	construction_bracingSpaceTitle: "Wall space for bracing",
	construction_bracingSpaceDetail: "Longest continuous wall section without a door or window, from floor to roof. These dimensions follow your openings. Sheathing, boundary studs and anchors need to be designed within this space.",
	construction_floorTitle: "5. Match the floor to its use",
	construction_floorDetail: "The standard sauna is planned without a shower or fixed water services. The floor still needs to withstand spills and intended cleaning, with protected wall junctions and a chosen way to manage water. Duckboards are a walking surface, not waterproofing.",
	construction_showerDetail: "A shower is outside the standard scope. It needs a complete wet-room system: drain, falls, waterproofing and sealed junctions, plus frost protection for water services. Selecting a shower does not make the current material list complete.",
	construction_connectionLocations: "Connection locations in the displayed roof",
	construction_connectionCountScope: "Counts follow the roof framing positions. These are locations requiring connection details, not quantities of brackets or screws.",
	construction_roofBearing: "Roof to wall",
	construction_ridge: "Rafter pairs at ridge",
	construction_baseAnchorage: "Frame to foundation",
	construction_notSpecified: "Depends on the selected foundation",
	construction_procurementScope: "Structural connectors, fasteners and additional roof moisture details are not specified in the purchase list. The existing fastener row is a budget allowance; these items have not been quantified or priced separately.",
	domain_build_constructionRoofPrereq: "Document the roof system, bearings and connections for the building dimensions and loads before assembling the roof. See the construction basis in Rules.",
	domain_build_constructionMoisturePrereq: "Specify the air path, wind protection and sealed junctions before closing the insulation. Follow a consistent outdoor-sauna assembly.",
	construction_fastenerBudget: 'Budget allowance — screws and connectors per joint remain to be specified',
	construction_showerScope: 'Shower: the drain, waterproofing, floor falls and installation are not included in materials or pricing. A designed wet-room assembly is required.',
	construction_unplannedCuts: 'Not included in optimised timber purchasing — requires a separate takeoff or cutting plan:',
	construction_openingInvalid: 'The construction has openings that need to be changed. Correct the flagged placements before cutting or ordering.',
	openings_error_framingClearance: 'There is not enough space for the opening framing. Increase the separation.',
	openings_error_headerClearance: 'The header and top plate do not fit. Lower the top of the opening.',
	openings_error_partitionCollision: 'The opening crosses the partition. Move the opening or partition.',
	openings_error_outOfBoundsStart: "Opening must start within the wall.",
	openings_error_outOfBoundsEnd:
		"Opening extends past the allowed wall area (max {wall} mm, corner clearance included).",
	openings_error_outOfBoundsHeight: "Opening exceeds wall height ({wall} mm).",
	openings_error_overlap: "Opening overlaps another on the same wall.",

	/* ── Deck Editor ─────────────────────────────────────── */
	deck_sectionLabel: "Deck / terrace",
	deck_intro:
		"Enable a deck in front of the sauna and tune its footprint, depth and framing directly in the design workspace.",
	deck_enable: "Add deck",
	deck_disabledState: "No deck selected yet.",
	deck_shape: "Shape",
	deck_shape_rectangular: "Rectangular",
	deck_shape_l_shape: "L-shape",
	deck_shape_wrap_front: "Wrap front",
	deck_shape_wrap_sides: "Wrap sides",
	deck_attachWall: "Attached to",
	deck_depth: "Depth",
	deck_width: "Width",
	deck_armDepth: "Arm depth",
	deck_armWidth: "Arm width",
	deck_wrapDepth: "Wrap depth",
	deck_boardType: "Deck board",
	deck_joistDimension: "Joist size",
	deck_boardOption_28x120: "28×120 mm",
	deck_boardOption_22x95: "22×95 mm",
	deck_joistOption_45x145: "45×145 mm",
	deck_joistOption_45x170: "45×170 mm",
	deck_boards: "Deck boards",
	deck_joists: "Joists",
	deck_bearers: "Bearers",
	deck_piers: "Piers",
	deck_screws: "Deck screws",
	deck_area: "Area",
	deck_estimatedCost: "Estimated cost",

	/* ── Roof Config Editor ───────────────────────────────── */
	roof_typeLabel: "Roof type",
	roof_pitchLabel: "Roof pitch",
	roof_pitchAriaLabel: "Roof pitch: {value} degrees",
	roof_gableDesc:
		"The gable roof runs a ridge along the building and slopes to left/right.",
	roof_monoDesc:
		"The shed roof keeps a single slope but can be adjusted for a steeper pitch.",
	roof_lowSlopeWarning:
		"Pitch below 14° is treated as low-slope and requires a special membrane or standing-seam system.",
	roof_systemLabel: "Roof system",
	roof_highWallLabel: "High side",
	roof_highWallFront: "Front",
	roof_highWallBack: "Back",
	roof_highWallLeft: "Left side",
	roof_highWallRight: "Right side",
	roof_systemFelt: "Roof felt on decking",
	roof_systemSheetMetal: "Standing seam/sheet metal on decking",
	roof_runoffFreeDrip: "Free drip edge",
	roof_packageHint:
		"Choose both roof type and covering so the external roof package is complete.",
	roof_layerDecking: "Decking boards",
	roof_layerUnderlay: "Underlay membrane",
	roof_accessoryEaveFlashing: "Eave flashing",
	roof_accessoryVergeFlashing: "Verge flashing",
	roof_accessoryRidgeCapping: "Ridge capping",
	roof_noteExternalOnly:
		"This status only covers the external roof package, not the full moisture safety of the roof build-up.",
	roof_noteLowSlope:
		"Low-slope roofs need extra care in seams, membrane detailing, and runoff.",
	roof_manualReviewUnsupported:
		"The selected roof system is not fully supported for this roof type.",

	/* ── Site Conditions Editor ───────────────────────────── */
	site_sectionLabel: "Site & foundation",
	site_reset: "Reset",
	site_plinthLengthLabel: "Pier length",
	site_minBuriedLabel: "Minimum burial depth",
	site_minBuriedAriaLabel: "Minimum burial depth: {value} millimetres",
	site_terrainLabel: "Terrain",
	site_terrainFlat: "Flat terrain",
	site_terrainSlope: "Sloped terrain",
	site_slopeAcrossLabel: "Cross slope",
	site_slopeAcrossAriaLabel: "Cross slope: {value} percent",
	site_slopeAlongLabel: "Longitudinal slope",
	site_slopeAlongAriaLabel: "Longitudinal slope: {value} percent",
	site_note:
		"The sauna is levelled. On sloped terrain the piers vary in visible height and burial depth.",

	/* ── Thermal Inputs Editor ────────────────────────────── */
	thermal_heaterLabel: "Heating:",
	thermal_heaterWood: "Wood-burning stove",
	thermal_heaterElectric: "Electric heater",
	thermal_heaterModelLabel: "Model",
	thermal_heaterModelNone: "None chosen – standard size",
	thermal_heaterModelOutside: "fired from outside",
	thermal_heaterModelSpec: "{kw} kW · {min}–{max} m³ · {w}×{d}×{h} mm",
	thermal_heaterModelOutsideNote:
		"Fired from the far side of the wall the heater stands against. The materials list adds fire board on both faces around the opening.",
	thermal_heaterModelSource:
		"Dimensions and safety distances from the manufacturer",
	thermal_glassAreaLabel: "Glass area (m²):",
	thermal_stoneAreaLabel: "Stone area (m²):",
	thermal_logCabin: "Uninsulated outdoor log cabin",
	equipment_title: 'Equipment',
	equipment_lighting: 'Sauna lighting',
	equipment_wallLight: 'Wall light',
	equipment_noLight: 'No lighting',
	equipment_showerShort: 'Shower',
	equipment_shower: 'Shower in the lounge',
	equipment_showerSpace: 'The shower needs a clear 90 × 90 cm area beside a solid wall, outside door access. Enlarge the lounge or move benches and openings.',
	equipment_showerScope: 'The shower placement is shown in 3D. Shower fittings, drain, waterproofing, floor slope and plumbing need separate specification and pricing.',
	thermal_shower: "Include shower / wash area",
	thermal_heaterPlacementLabel: "Chimney bay:",
	thermal_heaterPlacementFixed:
		"Barrel saunas and electric heaters keep a fixed placement without selectable joist bays.",

	/* ── Viewer 3D ────────────────────────────────────────── */
	viewer_todDaylight: "Switch to daylight",
	viewer_todEvening: "Switch to evening light",
	viewer_todLabel: "Sun",
	viewer_todAriaLabel: "Time of day",
	viewer_todMidday: "Midday",
	viewer_todGoldenHour: "Golden hour",
	viewer_todNight: "Night",
	viewer_blueprintOn: "Show realistic view",
	viewer_blueprintOff: "Show blueprint mode",
	viewer_qualityButton: "Render quality",
	viewer_qualityLabel: "Render quality",
	viewer_qualityAuto: "Auto (recommended)",
	viewer_qualityHigh: "High",
	viewer_qualityBalanced: "Balanced",
	viewer_qualityLow: "Low",
	viewer_hintDrag: "Drag to rotate",
	viewer_hintScroll: "Scroll to zoom",
	viewer_hintPan: "Right-click drag to pan",
	viewer_hintClose: "Close hint",
	viewer_legendToggle: "Legend",
	viewer_legendSupply: "Supply vent",
	viewer_legendExhaust: "Exhaust vent",
	viewer_legendFireboard: "Fire-rated board",
	viewer_legendBeam: "Treated beam",
	viewer_legendPier: "Concrete pier",
	viewer_loadingScene: "Building your sauna, layer by layer…",
	viewer_sceneReady: "The sauna is ready!",
	viewer_recovering: "Restoring 3D view…",
	viewer_recoveryFailedTitle: "The 3D view could not be restored",
	viewer_recoveryFailedBody:
		"Try again in low quality, or reload the page if the problem continues.",
	viewer_retry: "Retry viewer",
	viewer_reload: "Reload page",
	viewer_measurementWallTitle: "Wall",
	viewer_measurementDoorTitle: "Door",
	viewer_measurementWindowTitle: "Window",
	viewer_measurementPartitionTitle: "Partition wall",
	viewer_measurementPartitionSubtitle: "Room split",
	viewer_measurementWallFront: "Front wall",
	viewer_measurementWallBack: "Back wall",
	viewer_measurementWallLeft: "Left wall",
	viewer_measurementWallRight: "Right wall",
	viewer_measurementLength: "Length",
	viewer_measurementWidth: "Width",
	viewer_measurementHeight: "Height",
	viewer_measurementLeftOffset: "From left edge",
	viewer_measurementRightOffset: "From right edge",
	viewer_measurementBackOffset: "From back edge",
	viewer_measurementFrontOffset: "From front edge",
	viewer_measurementFrontDistance: "Distance from front wall",
	viewer_measurementBackDistance: "Distance from back wall",
	viewer_measurementSaunaDepth: "Sauna depth",
	viewer_measurementLoungeDepth: "Lounge depth",
	viewer_measurementBenchTitle: "Bench",
	viewer_measurementHeaterTitle: "Heater",
	viewer_measurementDepth: "Depth",
	viewer_measurementAlongWall: "Position along wall",
	viewer_measurementWallGap: "Gap to wall",
	viewer_measurementBenchStartEdge: "Bench start",
	viewer_measurementBenchEndEdge: "Bench end",
	viewer_interiorEditOn: "Close interior editing",
	viewer_interiorEditOff: "Edit the interior",
	viewer_groupViews: "Views",
	viewer_groupDisplay: "Display",
	viewer_sectionToggleOn: "Close section view",
	viewer_sectionToggleOff: "Show section view",
	viewer_scaleFiguresOn: "Hide scale figures",
	viewer_scaleFiguresOff: "Show scale figures",
	viewer_scaleFiguresSeat: "≈ {count} seat",
	viewer_scaleFiguresSeats: "≈ {count} seats",
	viewer_scaleFiguresRelaxSeats: "+ {count} in relax room",
	viewer_ambienceOn: "Turn sound off",
	viewer_ambienceOff: "Turn sound on",
	viewer_rotationGroup: "Orientation",
	viewer_rotationTitle: "Turn",
	viewer_rotationHandle:
		"Turn the sauna — drag around, release to the nearest quarter turn",
	viewer_rotationLeft: "Turn a quarter turn anticlockwise",
	viewer_rotationRight: "Turn a quarter turn clockwise",
	viewer_rotationValue: "The sauna stands turned {deg}° on the plot",
	viewer_rotationPausedInSection: "Turning is paused in section view",
	site_orientationLabel: "turned {deg}° on the plot",
	viewer_sectionAxisLabel: "Cut direction",
	viewer_sectionAxisWidth: "Across width",
	viewer_sectionAxisLength: "Across length",
	viewer_sectionPositionLabel: "Cut position",
	viewer_sectionLegendTitle: "Wall layers in the cut",
	viewer_sectionMeasurementPaused: "Measurement is paused in section view.",
	viewer_sectionLayerExterior: "Exterior cladding",
	viewer_sectionLayerFraming: "Timber framing (stud)",
	viewer_sectionLayerPanel: "Interior panel",
	viewer_sectionLayerFoundation: "Pier foundation",
	viewer_sectionLayerRoof: "Roof layers",
	viewer_sectionLayerBench: "Bench",
	viewer_sectionLayerGlass: "Glass",
	viewer_sectionLayerInsulation: "Insulation & vapour barrier",
	instr_stepViewGroupFoundation: "foundation",
	instr_stepViewGroupDeck: "deck",
	instr_stepViewGroupFraming: "framing",
	instr_stepViewGroupPanel: "panel",
	instr_stepViewGroupRoof: "roof",
	instr_stepViewGroupInterior: "interior",
	instr_stepViewGroupExterior: "facade",
	instr_snapshotUnavailable: "3D snapshot unavailable in this export.",
	instr_snapshotCamera: "Camera",
	instr_snapshotVisibleGroups: "Visible groups",
	instr_cameraPresetHero: "Hero",
	instr_cameraPresetInterior: "Interior",
	instr_cameraPresetSide: "Side",
	viewer_helpToggle: "Help and legend",
	viewer_helpOpen: "Open help",
	viewer_helpControlsTitle: "Controls",
	viewer_helpLegendTitle: "Legend",
	viewer_placementWindowTitle: "Placing window",
	viewer_placementHint: "Click an exterior wall to place the window.",
	viewer_placementCancel: "Cancel placement",

	/* ── Visibility Bar ───────────────────────────────────── */
	vis_exterior: "Exterior",
	vis_roof: "Roof",
	vis_interior: "Interior",
	vis_framing: "Framing",
	vis_panel: "Cladding",
	vis_frontWall: "Front wall",
	vis_foundation: "Foundation",
	vis_deck: "Deck",
	vis_heaterClearance: "Safety clearance",
	vis_ventilation: "Ventilation",
	vis_groupShell: "Shell",
	vis_groupStructure: "Structure",
	vis_groupAnalysis: "Analysis",
	vis_visible: "visible",
	vis_hidden: "hidden",
	/** Verb used as a tooltip / aria prefix on the visibility-bar buttons. */
	vis_actionShow: "Show",
	vis_actionHide: "Hide",

	/* ── Material Pane ────────────────────────────────────── */
	mat_statTransport: "Transport",
	mat_statBudget: "Budget",
	mat_statPhases: "Phases",
	mat_viewPhase: "By build phase",
	mat_viewAll: "Full list",
	mat_viewAriaLabel: "List view",
	mat_jumpToPricing: "See pricing",
	mat_colMaterial: "Material",
	mat_colDimension: "Dimension",
	mat_colQuantity: "Quantity (approx)",
	mat_weightCalcLabel: "Weight & transport",
	mat_weightHeavy: "Heavy load",
	mat_weightCheck: "Check",
	mat_weightBuildingNet: "Building net",
	mat_weightBuildingGross: "Building gross (+10%)",
	mat_weightPlinths: "Piers",
	mat_weightTransportTotal: "Total transport weight",
	mat_elZoneLabel: "Electrical zone calc",
	mat_elZoneHot: "Hot zone",
	mat_elZoneDangerTitle: "DANGER TO LIFE",
	mat_elZoneDangerBody:
		"Standard PVC cable melts at ~70 °C. ALWAYS use silicone cable (SiHF 180 °C) above 500 mm from the heater!",
	mat_elColComponent: "Component",
	mat_elColCableType: "Cable type",
	mat_elColZone: "Zone",
	mat_elZoneHotLabel: "Hot (>500 mm)",
	mat_elZoneColdLabel: "Cold (≤500 mm)",

	/* ── Cut Pane ─────────────────────────────────────────── */
	cut_kingStud: "Full-height opening stud",
	cut_jackStud: "Header support stud",
	cut_crippleStud: "Short opening stud",
	cut_longSide: "long side",
	cut_shortSide: "short side",
	cut_oneDirection: "one direction",
	cut_squareEnd: "square cut",
	cut_intro: "Choose a build phase. All cut lengths are in millimetres. Use the cutting diagram to choose stock lengths.",
	cut_siteWork: "Site cuts or separate planning",
	cut_other: "Other parts",
	purchase_rows: "rows",
	purchase_title: "Your shopping list",
	purchase_intro: "Open a build phase for materials, dimensions and quantities. Switch to the full list for an overview.",
	cut_title: "Your cut list",
	cut_colPart: "Part",
	cut_colCount: "Count",
	cut_colLength: "Cut length",
	cut_footnote:
		"* For the front door opening, 2–3 vertical studs are adapted and cut to match the exact door frame dimensions (typically 710 × 1910 mm).",

	/* ── Cut Diagram (Visual Optimizer) ──────────────────── */
	cutDiagram_title: "Cutting diagram",
	cutDiagram_boards: "Boards to buy",
	cutDiagram_waste: "Waste",
	cutDiagram_stockLength: "Stock length",
	cutDiagram_selectDimension: "Select dimension",
	cutDiagram_piece: "Piece",
	cutDiagram_remaining: "Remaining",
	cutDiagram_boardLabel: "Board {n}",
	cutDiagram_noCuts: "No cuts for this dimension",
	cutDiagram_allDimensions: "All dimensions",
	cutDiagram_overrideHint: "Change length if your planks differ",
	cutDiagram_sawKerf: "Saw kerf",
	cutDiagram_legend: "Legend",

	/* ── Price Pane ───────────────────────────────────────── */
	price_totalLabel: "Total price",
	price_liveNote: "Live pricing when available, otherwise local estimate.",
	price_localNote: "Local estimate based on materials and rules.",
	price_driverLabel: "Cost driver",
	price_driverWood: "Wood-burning setup",
	price_driverElectric: "Electric heater",
	price_openingSingular: "opening",
	price_openingPlural: "openings",
	price_chimneyIncluded: "chimney included",
	price_nextStepLabel: "Next step",
	price_nextStepFallback: "The build plan is next",
	price_openShop: "Open store",
	price_goToBuildPlan: "Go to build plan",
	price_estimateTitle: "Price estimate",
	price_productRecommendations: "Products that fit this design",
	price_heatersMatch: "{count} heaters match the current power requirement.",
	price_openProduct: "Open product →",
	price_complementWith: "Complement with",
	price_chimneyPackage: "Chimney package",
	price_chimneySystem: "System:",
	price_chimneyDiameter: "Diameter:",
	price_chimneyHeight: "Total height:",
	price_fetchingLive: "Fetching live prices...",
	price_staleData: "Price data is partially outdated.",
	price_staleReason: "Reason:",
	price_colProduct: "Product",
	price_colQuantity: "Quantity",
	price_colPrice: "Price (approx)",
	price_totalLive: "Total at today's store prices",
	price_totalLocal: "Material cost",
	price_materialMode: "Material cost",
	price_diyMode: "DIY budget",
	price_marketMode: "Market comparison",
	price_modeNoteMaterial: "Every row in the list below, added up at catalogue prices.",
	price_modeNoteDiy:
		"Materials plus the steps that need a professional, delivery, and the small parts every build uses up.",
	price_modeNoteMarket:
		"Roughly what a finished sauna package costs: your budget plus prefabrication, sales and coordination.",
	price_budgetsSummary: "What the whole project may cost",
	price_diyDelta: "{amount} on top of materials",
	price_marketDelta: "{amount} more than building it yourself",
	price_comparatorNote:
		"The market comparison is not a quote, only a benchmark showing why a finished package can land above raw material cost.",
	price_colMaterial: "Material",
	price_colUsage: "Usage",
	price_goToShop: "Go to shopping list at store →",
	price_footnote:
		"* Prices are estimates incl. VAT. Live prices update nightly and may vary by region.",
	price_currencyDisclaimer:
		"Prices shown in EUR are approximate conversions from SEK at a fixed rate. Actual prices depend on local retailers.",

	/* ── Instructions Pane ────────────────────────────────── */
	instr_title: "Build instructions: Step-by-step (outdoor sauna)",
	instr_timeEstimate: "⏱️ Time estimate",
	instr_area: "Area:",
	instr_totalTime: "Total estimated time:",
	instr_weekendPlan: "Plan for:",
	instr_weekendUnit: "weekends",
	instr_weekendPerStep: "Plan {label} for this step.",
	instr_proSteps: "Professional steps:",
	instr_proStepsDesc: "{pro} of {total} steps require a professional",
	instr_beforeStart: "Important before you start",
	instr_markStep: "Mark step {title} as complete",
	instr_timeLabel: "⏱️ approx {hours} hours",
	instr_dependsOn: "Complete first:",
	instr_prerequisites: "Prerequisites",
	instr_checklist: "Checklist",
	instr_materialsCheck: "Materials to double-check",
	instr_cutPrep: "Cut list / preparation",
	instr_risks: "Risks",
	instr_controls: "Quality checks",
	instr_tools: "Tools",
	instr_crewSolo: "1 person",
	instr_crewDuo: "2 people",
	instr_calloutSafety: "Safety check",
	instr_calloutQuality: "Quality check",
	instr_wrong: "Wrong",
	instr_right: "Right",
	instr_measurements: "Measurements to lock",
	instr_allSteps: "All steps",
	instr_previousStep: "Previous step",
	instr_journeyPosition: "Step {current} of {total}",
	instr_planning: "Planning & preparation",
	instr_projectBasis: "Project details",
	instr_progressLabel: "{done} of {total} steps completed",
	instr_stepDoneToast: "Step {n} done — {done} of {total}",
	instr_statusDone: "Done",
	instr_statusProgress: "In progress",
	instr_statusReady: "Ready",
	instr_nextStep: "Next step",
	instr_showCompleted: "Show {count} completed steps",
	instr_hideCompleted: "Hide completed steps",
	instr_calendarEstimate: "Calendar time:",
	instr_gateKindPermit: "Permits",
	instr_gateKindBooking: "Book now",
	instr_gateKindInspection: "Inspection",
	instr_gateBlocksBefore: "Must clear before: {title}",
	instr_gateLeadTime: "Lead time: {label}",
	instr_dryingHours: "+{hours} h drying",
	instr_weatherCritical: "Weather critical",
	instr_mobileProgress: "Substep {current} of {total}",
	instr_mobileDoneCount: "{done} done",
	instr_mobileStepIndex: "Step {step} of {total}",
	instr_mobileSubstepIndex: "{current} of {total}",
	instr_mobileComplete: "Mark substep complete",
	instr_mobilePrev: "Previous",
	instr_mobileCrew: "Crew",
	instr_mobileCallout: "Watch closely",
	instr_mobileCalloutWrong: "Wrong:",
	instr_mobileCalloutRight: "Right:",
	instr_mobileView: "3D view / measurement cues",
	instr_mobileViewUnavailable:
		"The 3D step view is not available here yet. Follow the checklist and measurement cues in text for now.",
	instr_mobileDiagram: "Diagram",
	instr_mobileGateChecklist: "Gate checklist",

	/* ── BBR Pane ─────────────────────────────────────────── */
	bbr_buildStatus: "Build status",
	bbr_checkRequired: "Check required",
	bbr_fireNotificationYes: "Fireplace notification required.",
	bbr_fireNotificationNo: "No separate fireplace notification flagged.",
	bbr_placementLabel: "Placement",
	bbr_boundaryDistance: "{distance} m to property boundary",
	bbr_verifyBoundary: "Verify property boundary",
	bbr_fireDistance: "{distance} m fire safety gap to nearest building.",
	bbr_verifyDistances:
		"Check distances to property boundary and other buildings before you start.",
	bbr_riskFirst: "Risk first",
	bbr_riskWood: "Fireplace and chimney",
	bbr_riskElectric: "Electrical and ventilation",
	bbr_riskWoodDesc:
		"Safety distances, chimney inspection, and notification must be ready first.",
	bbr_riskElectricDesc:
		"Zone classification, cable selection, and ventilation must be secured before installation.",
	bbr_heaterTitle: "Recommended heater output",
	bbr_effectiveVolume: "Effective volume:",
	bbr_recommendedHeater: "Recommended {type}:",
	bbr_heaterTypeWood: "stove",
	bbr_heaterTypeElectric: "heater",
	bbr_electricalReq: "{type}:",
	bbr_warning: "Warning:",
	bbr_permitTitle: "Permit guidance",
	bbr_permitStatus: "Status:",
	bbr_permitArea: "Area:",
	bbr_fireNotification: "Fireplace notification:",
	bbr_placementTitle: "Site placement",
	bbr_boundary: "Property boundary:",
	bbr_fireGap: "Fire safety gap:",
	bbr_glassTitle: "Glass penalty – Windows & heat loss",
	bbr_glassArea: "Total glass area:",
	bbr_effectiveVolumeAdj: "Effective volume (adjusted):",
	bbr_glassRequired: "Required:",
	bbr_statusSufficient: "Sufficient",
	bbr_statusInsufficient: "Insufficient",
	bbr_benchTitle: "Bench ergonomics",
	bbr_benchIntro:
		'Two models: Finnish (löyly rule, top-down) and Swedish (bottom-up). Top-down recommended ("bench height measured from ceiling").',
	bbr_benchUpperFi: "🇫🇮 Upper bench (Finnish):",
	bbr_benchLowerFi: "🇫🇮 Lower bench (Finnish):",
	bbr_benchUpperSe: "🇸🇪 Upper bench (Swedish):",
	bbr_benchLowerSe: "🇸🇪 Lower bench (Swedish):",
	bbr_loylyStatus: "Löyly status:",
	bbr_loylyPass: "Pass",
	bbr_loylyFail: "Fail",
	bbr_minCeiling: "Min. ceiling height:",
	bbr_roofJoistsTitle: "Roof joists & snow zone",
	bbr_roofAssemblyTitle: "External roof completeness",
	bbr_roofAssemblyComplete: "Complete",
	bbr_roofSystemLabel: "Roof package:",
	bbr_roofAreaLabel: "Sloped roof area:",
	bbr_roofLayersLabel: "Layers:",
	bbr_roofAccessoriesLabel: "Required flashings:",
	bbr_snowZone: "Snow zone:",
	bbr_snowZoneIndoor: "Not applicable indoors – the house carries the snow load",
	bbr_placementIndoorDesc: "No distances to the boundary or other buildings. The sauna stands in an existing room.",
	bbr_placementIndoor: "Indoor",
	bbr_joistDimension: "Dimension:",
	bbr_joistSpacing: "Spacing:",
	bbr_joistOk:
		"Standard timber (C24) is sufficient for this snow zone and span.",
	bbr_fireTitle: "Fire safety (chimney inspector requirements)",
	bbr_safetyDistance: "Safety distance:",
	bbr_sparkGuard: "Spark guard:",
	bbr_chimneyLabel: "Chimney:",
	bbr_drainTitle: "Drainage",
	bbr_floorDrainRequired: "Required",
	bbr_floorDrainNotRequired: "Normally not required",
	bbr_floorSlope: "Floor slope:",
	bbr_ventTitle: "Ventilation (calculated strategy)",
	bbr_ventIntake: "🔵 Supply air (intake):",
	bbr_ventExhaust: "🔴 Exhaust air:",
	bbr_ventMinArea: "Min. area per vent:",
	bbr_ventFanRequired: "Required ✓",
	bbr_ventFanNotRequired: "Not required",
	bbr_doorTitle: "Door safety (building code)",
	bbr_touchGuardTitle: "Touch guard",
	bbr_maxTempTitle: "Maximum temperature",
	bbr_moistureTitle: "Moisture & vapour barrier (outdoor sauna)",
	bbr_elZoneTitle: "Electrical cables & zone classification",
	bbr_hotZone: "Hot zone (above {mm} mm):",
	bbr_coldZone: "Cold zone (below {mm} mm):",
	bbr_transformers: "Transformers:",
	bbr_ceilingLights: "Ceiling lights:",
	bbr_ceilingLightsDesc:
		"Spotlights and wall lights above sauna benches always require silicone cable and heat-resistant junction boxes.",
	bbr_cableSpecTitle: "Cable specification",
	bbr_cableType: "Cable:",
	bbr_fuseLabel: "Fuse:",
	bbr_rcdLabel: "RCD:",
	bbr_sihfLength: "SiHF in hot zone:",
	bbr_totalCableLength: "Total cable length:",
	bbr_sectionDanger: "Danger",
	bbr_sectionWarning: "Warning",

	/* ── Rule body text (rendered in BBR pane) ────────────── */
	rule_fireSafetyDistance:
		"A wood-burning heater typically requires 500 mm clearance to combustible material. Halve this distance by installing fire-rated boards (calcium silicate) with an air gap.",
	rule_sparkGuard:
		"Sheet metal (hearth plate) under the heater, extending at least 300 mm in front of the stove door.",
	rule_chimney:
		"Insulate with non-combustible stone wool, 100 mm safety clearance to combustible roof joists.",
	rule_ventilationIntake: "Low at floor level, directly at/behind the heater.",
	rule_ventilationExhaust: "High up, diagonally opposite the heater.",
	rule_moistureBarrierOutdoor:
		"This model uses mineral wool and sauna foil on the warm side. Follow a consistent sauna system with temperature-rated products and sealed seams and penetrations. The requirement is a moisture-safe assembly; sauna foil is not a universal material requirement for every sauna type. Room-within-a-room, solid timber and other insulation systems need their own details. Design outer-roof ventilation and wind protection separately.",
	rule_doorSafety:
		"The sauna door opens outwards and has no lock or latch — a locked door can trap someone in the heat. Use a magnetic catch or roller latch and a wooden handle. Leave a 3–4 cm gap under the door for ventilation, and enough clearance around the frame that heat expansion cannot jam it. Window glass must be tempered.",
	rule_maxTemperature:
		"The heater must be designed so that the air temperature does not exceed 110 °C. Consider a thermostat/timer as an extra safety measure.",
	rule_touchGuard:
		"A touch guard (wooden railing/cage) must surround the heater to prevent accidental contact with hot surfaces. NOTE: this is NOT the same as fire-rated boards (wall protection) — both are required!",
	rule_siliconeCableType:
		"Heat-resistant silicone cable (SiHF 3×1.5 mm², 180 °C)",
	rule_standardCableType: "Standard cable (EKK 3×1.5 mm²)",
	rule_transformerWarning:
		"Keep the transformer outside the sauna, unless it is mounted at floor level (below 500 mm).",
	rule_mechanicalIntakeBan:
		"Mechanical supply-air ventilation into a freestanding sauna is strongly discouraged. Overpressure can force hot, humid air into the wall structure and cause moisture damage. Supply air should enter via natural draft (gap under the door or a vent). Exception: a sauna inside a dwelling with an HRV system may use mechanical supply air if the system is correctly sized.",

	/* ── Foundation Diagram Pane ──────────────────────────── */
	found_title: "Foundation plan (top view)",
	found_outerDimensions: "Outer dimensions:",
	found_pierCount: "Number of piers:",
	found_pierUnit: "pcs",
	found_pierAriaLabel: "Pier {label}",
	found_beamCount: "Number of beams:",
	found_piersPerBeam: "Piers per beam:",
	found_plinthLength: "Selected pier length:",
	found_minBuried: "Min. burial depth:",
	found_warnings: "Warnings:",
	found_digGuide: "Dig guide:",
	found_digInstructions:
		"Measure out the sauna footprint ({width} × {length} mm) on the ground. Pier positions are measured from the top-left corner:",
	found_pierFromLeft: "from left edge",
	found_pierFromFront: "from front edge",
	found_pierVisible: "visible pier",
	found_pierBuried: "buried part",
	found_pierRecommended: "recommended pier",
	found_checkPlinth: "Check pier length:",
	found_specialSolution:
		"The slope requires a special solution beyond the 900 mm standard pier.",
	found_chooseMinPlinth: "Choose at least {length} mm pier.",
	found_colPier: "Pier",
	found_colFromLeft: "X (from left)",
	found_colFromFront: "Z (from front)",
	found_colGroundLevel: "Ground level",
	found_colVisibleHeight: "Visible height",
	found_colBuriedPart: "Buried part",
	found_colStatus: "Status",
	found_statusOk: "OK",
	found_statusWarning: "Warning",
	found_selectPierHint:
		"Select a pier in the diagram to see specific measurements.",
	found_emptySelectionHint:
		"Dig guide — click the piers in the sketch above to see specific instructions for each pier.",

	/* ── Framing Diagram Pane ────────────────────────────── */
	framing_diagramTitle: "Wall framing",
	framing_headerSpanWarning:
		"An opening spans further than the header table covers (1.5 m). The shown header is not sufficient — an engineered glulam beam is required.",
	framing_frontWall: "Front wall",
	framing_backWall: "Back wall",
	framing_leftWall: "Left wall",
	framing_rightWall: "Right wall",
	framing_dividerWall: "Partition wall",
	framing_studCount: "Studs",
	framing_dimensions: "Dimensions",

	/* ── Domain: Build Steps ───────────────────────────────── */
	domain_build_foundationTitle: "Foundation & floor framing",
	domain_build_drainageTitle: "Drainage & plumbing",
	domain_build_deckTitle: "Deck & terrace",
	domain_build_framingTitle: "Wall framing",
	domain_build_roofTitle: "Roof & weather protection",
	domain_build_facadeTitle: "Exterior cladding",
	// ── Character choices (issue 134) ─────────────────────────
	domain_build_facadeTreatmentTitle: "Facade treatment",
	domain_build_timeline_treatmentWeatherReason:
		"Brush in dry weather above +5 °C — rain within the drying time washes the treatment off.",
	domain_build_guide_facadeSubstepTreatment: "Coat the facade",
	domain_build_guide_calloutTreatmentWrong:
		"Waiting a season, then coating cladding that has already greyed unevenly.",
	domain_build_guide_calloutTreatmentRight:
		"Coat as soon as the cladding is up and dry — end grain and loose board backs included.",
	domain_build_guide_facadeCheckpointTreatmentCoverage:
		"Whole facade coated, corner boards, casings and base trim included.",
	domain_build_guide_facadeCheckpointTreatmentDrying:
		"Drying time observed before the next coat or final inspection.",
	tool_facadeBrush: "Wide facade brush",
	tool_paintBucketAndStirrer: "Paint bucket & stirrer",
	tool_dropCloth: "Drop cloth / masking",
	finish_sectionTitle: "Character",
	finish_sectionIntro:
		"Wood species, panel direction and exterior colour. Every choice shows in 3D and carries through the material list, cut list and price.",
	finish_woodGroupLabel: "Interior wood",
	finish_directionGroupLabel: "Interior panel direction",
	finish_treatmentGroupLabel: "Exterior colour",
	finish_deltaIncluded: "Included",
	finish_wood_asp: "Aspen",
	finish_wood_al: "Alder",
	finish_wood_gran: "Spruce",
	finish_wood_aspDescription:
		"Pale, knot-free and cool to the touch — the classic sauna wood.",
	finish_wood_alDescription:
		"Warm red-brown that deepens with age. Soft surface, cool to sit on.",
	finish_wood_granDescription:
		"Cheapest and pale yellow. Can bleed resin in the heat — pick aspen or alder for benches you sit on directly.",
	finish_direction_standing: "Vertical",
	finish_direction_lying: "Horizontal",
	finish_direction_standingDescription:
		"Boards run floor to ceiling. Adds height, and the most common choice in Nordic saunas.",
	finish_direction_lyingDescription:
		"Boards run along the walls. Makes the room read wider — the battens behind turn vertical.",
	finish_treatment_untreated: "Untreated",
	finish_treatment_tjarvitriol: "Tar vitriol",
	finish_treatment_jarnvitriol: "Iron vitriol",
	finish_treatment_slamfargRed: "Red distemper",
	finish_treatment_slamfargBlack: "Black distemper",
	finish_treatment_untreatedDescription:
		"The spruce greys on its own. Nothing to coat, nothing to maintain.",
	finish_treatment_tjarvitriolDescription:
		"Deep warm brown. One coat, spreads about 7 m² per litre.",
	finish_treatment_jarnvitriolDescription:
		"Steers the greying to an even silver. One coat, cheapest per m².",
	finish_treatment_slamfargRedDescription:
		"Falu red with a matte, chalky surface. Two coats.",
	finish_treatment_slamfargBlackDescription:
		"Black with a matte, chalky surface. Two coats.",
	term_saunaPanel: "Sauna panel",
	term_exteriorTreatmentTjarvitriol: "Tar vitriol (facade treatment)",
	term_exteriorTreatmentJarnvitriol: "Iron vitriol (facade treatment)",
	term_exteriorTreatmentSlamfargRed: "Red distemper (facade treatment)",
	term_exteriorTreatmentSlamfargBlack: "Black distemper (facade treatment)",
	term_qty_litre: "litres",
	term_cut_interiorPanelLyingRuns: "Front/back {width} mm, sides {length} mm",
	term_dim_treatmentCoatsSingle: "{coats} coat, {coverage} m²/litre",
	term_dim_treatmentCoatsPlural: "{coats} coats, {coverage} m²/litre",
	term_cut_interiorPanelStanding: "Sauna panel, vertical",
	term_cut_interiorPanelLying: "Sauna panel, horizontal",
	domain_build_insulationTitle: "Insulation & interior cladding",
	domain_build_benchesTitle: "Benches & interior fitout",
	domain_build_systemsTitle: "Heater, ventilation & electrical",
	domain_build_chimneyTitle: "Chimney & fire protection",
	domain_build_handoverTitle: "Final inspection & handover",
	domain_build_framingDesc:
		"Assemble walls flat on ground, raise with bracing and leave mounting points for benches and openings.",
	domain_build_roofDesc:
		"Install roof joists, sheathing, membrane and fascia trim before exterior cladding closes the structure.",
	domain_build_facadeDesc:
		"Build air gap, wind barrier, exterior cladding and drip edge so the sauna can dry out after use without boxing in the foundation.",
	domain_build_insulationDesc:
		"Fill wall and roof cavities, install interior cladding with ventilation gap behind and leave expansion joints.",
	domain_build_benchesDesc:
		"Build multi-level benches with rounded edges and concealed fixings for optimal comfort.",
	domain_build_systemsDescWood:
		"Position the wood-burning stove, install fire-rated boards and ventilation before the chimney is fitted separately.",
	domain_build_systemsDescElectric:
		"Prepare electric heater, cable routing, residual-current device (RCD) and ventilation per electrical specification.",
	domain_build_foundationDescDefault:
		"Set out concrete piers/beams, install floor framing and ensure a capillary-break layer.",
	domain_build_drainageDescShower: "Plan shower drainage early.",
	domain_build_drainageDescNoShower:
		"Secure ground drainage before the floor is closed.",
	domain_build_deckDesc:
		"Build the deck while the foundation is still accessible and verify joist spacing, bearers and board gaps before the final row is fixed.",
	domain_build_chimneyDesc:
		"Install chimney, maintain correct roof penetration clearance and follow fire-safety requirements.",
	domain_build_handoverDescWood:
		"Book a chimney sweep for pressure test / final inspection before the first fire.",
	domain_build_handoverDescElectric:
		"Run a function check of heater, ventilation and door safety before the first sauna session.",
	domain_build_badgeDiy: "✅ DIY",
	domain_build_badgeDiyReason:
		"This step is normally suitable for a handy builder.",
	domain_build_badgeElectrician: "⚠️ Hire an electrician",
	domain_build_badgeElectricianReason:
		"Legal requirement: electric heaters must be connected by a licensed electrician.",
	domain_build_badgeChimneySweep: "⚠️ Hire a chimney sweep / installer",
	domain_build_badgeChimneyReason:
		"Legal requirement: fire-safe chimney installation and inspection is required for wood-burning setups.",
	domain_build_badgePlumber: "⚠️ Plumber may be needed",
	domain_build_badgePlumberReason:
		"Recommendation: a wet area with floor drain and plumbing usually requires professional support.",
	domain_build_badgeSystemsDiyReason:
		"Stove placement, ventilation and fire-rated boards can normally be prepared by the builder before the chimney step.",
	domain_build_toolGroupCommon: "Common household tools",
	domain_build_toolGroupSpecialist: "Need to buy / rent",

	/* ── Domain: Build — Tool Names ──────────────────────── */
	tool_measuringStick: "Folding rule",
	tool_chalkLine: "Chalk line",
	tool_screwdriver: "Drill/driver",
	tool_earthAugerOrSpade: "Earth auger or spade",
	tool_laserLevel: "Laser level",
	tool_mitreSaw: "Mitre/chop saw",
	tool_spiritLevel: "Spirit level",
	tool_clamps: "Clamps",
	tool_hammer: "Hammer",
	tool_nailGunOrScrewdriver: "Nail gun or drill/driver",
	tool_ladder: "Ladder",
	tool_utilityKnife: "Utility knife",
	tool_nailGun: "Nail gun",
	tool_fallProtection: "Fall protection for larger saunas",
	tool_carpenterSquare: "Carpenter's square",
	tool_nailPunch: "Nail punch",
	tool_panelSaw: "Panel saw",
	tool_scaffoldOrTrestles: "Scaffold or trestles",
	tool_insulationKnife: "Insulation knife",
	tool_protectiveGloves: "Protective gloves",
	tool_panelClipTool: "Panel clip tool",
	tool_plungeSaw: "Plunge saw",
	tool_sandingBlock: "Sanding block",
	tool_routerRoundedEdges: "Router for rounded edges",
	tool_tapeMeasure: "Tape measure",
	tool_drillPress: "Drill",
	tool_multimeter: "Multimeter",
	tool_torqueScrewdriver: "Torque screwdriver",
	tool_tinSnips: "Tin snips",
	tool_spade: "Spade",
	tool_straightedge: "Straightedge",
	tool_groundCompactor: "Plate compactor",
	tool_pipeCutter: "Pipe cutter",
	tool_rivetTool: "Rivet tool",
	tool_chimneyPipeCutter: "Chimney pipe cutter",
	tool_scaffoldOrFallProtection: "Scaffold or fall protection",
	tool_checklist: "Checklist",
	tool_mobileCamera: "Phone camera",
	tool_moistureMeterOptional: "Moisture meter (optional)",
	tool_spacers5mm: "5 mm spacers",
	tool_fireboardCutter: "Fireboard cutter",
	tool_ventDrillJig: "Vent hole drill jig",
	tool_groundFabric: "Ground fabric",
	tool_wheelbarrow: "Wheelbarrow",
	tool_stapleGun: "Staple gun",
	tool_aluminiumTape: "Aluminium tape",
	tool_rubberMallet: "Rubber mallet",
	tool_bandTensioner: "Band tensioner",
	tool_glassSuctionLifter: "Glass suction lifter",

	/* ── Domain: Build — Partition Description ───────────── */
	domain_build_partitionBlocking:
		"The partition lands between side-wall studs and needs {count} blocking piece in the exterior wall for proper anchoring.",
	domain_build_partitionBlockingPlural:
		"The partition lands between side-wall studs and needs {count} blocking pieces in the exterior walls for proper anchoring.",
	domain_build_foundationPlintsDeeper: "{count} piers require deeper holes.",
	domain_build_guide_kickoffTitle: "Build start",
	domain_build_guide_kickoffSummary:
		"Lock site layout, foundation, and critical services before you begin the step-by-step build guide.",
	domain_build_guide_kickoffHighlightFoundation: "Foundation",
	domain_build_guide_kickoffHighlightFoundationValue: "{count} piers",
	domain_build_guide_kickoffHighlightOpenings: "Openings",
	domain_build_guide_kickoffHighlightOpeningsValue: "{count} openings",
	domain_build_guide_kickoffHighlightTime: "Time",
	domain_build_guide_kickoffHighlightTimeValue: "~{hours} hours",
	domain_build_guide_kickoffHighlightProfessional: "Pro steps",
	domain_build_guide_kickoffHighlightProfessionalValue: "{count} pro steps",
	domain_build_guide_foundationPurpose:
		"Build a straight, well-drained foundation that carries the full load before the rest of the kit is locked in.",
	domain_build_guide_foundationPrereqPermitOk:
		"Confirm the conditions for {permitLabel} are met before excavation.",
	domain_build_guide_foundationPrereqPermitRequired:
		"Confirm {permitLabel} and start approval before excavation.",
	domain_build_guide_foundationPrereqPlacement:
		"Set out the position on site and verify clearances to boundaries and nearby buildings.",
	domain_build_guide_foundationCheckpointMarkPiers:
		"Mark out {count} pier positions from the foundation drawing.",
	domain_build_guide_foundationCheckpointMarkPiersFallback:
		"Mark out all pier positions from the foundation drawing.",
	domain_build_guide_foundationCheckpointBeams:
		"Verify that {count} bearer lines sit on the correct centerlines.",
	domain_build_guide_foundationCheckpointDrainLayer:
		"Prepare ground fabric, gravel, and capillary break before the floor framing is closed.",
	domain_build_guide_foundationMaterialsPiers: "{count} piers total.",
	domain_build_guide_foundationMaterialsBeams:
		"{count} bearers with about {length} linear meters total.",
	domain_build_guide_foundationFallbackMaterials:
		"Confirm foundation materials against the purchase list.",
	domain_build_guide_foundationFallbackCuts:
		"Check the cut list for bearers and floor framing before cutting.",
	domain_build_guide_foundationRiskSkew:
		"Skewed pier lines cause knock-on errors in framing, cladding, and door alignment.",
	domain_build_guide_foundationControlDiagonal:
		"Double-check diagonals before piers or pads are locked in.",
	domain_build_guide_deckPurpose:
		"Build the deck while the foundation is still fully accessible and before wall framing blocks the work area.",
	domain_build_guide_deckPrereq:
		"Foundation lines and pier placement must be confirmed before joists and bearers are installed.",
	domain_build_guide_deckCheckpointLayout:
		"Check diagonal measurements and outside edges before deck boards are fixed.",
	domain_build_guide_deckCheckpointCc:
		"Verify joist spacing c/c {cc} mm and pier spans against the deck calculation.",
	domain_build_guide_deckCheckpointCcFallback:
		"Verify joist spacing and pier spans against the deck calculation.",
	domain_build_guide_deckCheckpointBoards:
		"Fix {count} deck boards with an even gap and fastening into every joist.",
	domain_build_guide_deckCheckpointBoardsFallback:
		"Fix the deck boards with an even gap and fastening into every joist.",
	domain_build_guide_deckSubstepBearers: "Bearers & supports",
	domain_build_guide_deckSubstepJoists: "Joists & spacing",
	domain_build_guide_deckSubstepBoards: "Board installation",
	domain_build_guide_deckMeasurementBearers:
		"{count} bearers {dimension} on the pier supports.",
	domain_build_guide_deckMeasurementJoists:
		"{count} joists {dimension} at c/c {cc} mm.",
	domain_build_guide_deckBoardMaterialFallback:
		"Confirm deck boards against the purchase list before you start fixing them.",
	domain_build_guide_calloutDeckBoardWrong:
		"Boards laid with no gap or touching the ground wick up moisture and rot.",
	domain_build_guide_calloutDeckBoardRight:
		"Leave a 4–6 mm gap and keep the deck clear of the ground so it can dry.",
	domain_build_guide_crewReasonDeckBearers:
		"Two people keep the bearers level while aligning them on the pier supports.",
	domain_build_guide_deckFallbackMaterials:
		"Confirm deck materials against the purchase list before starting.",
	domain_build_guide_deckFallbackCuts:
		"Check the cut list for deck boards, joists, and bearers.",
	domain_build_guide_deckRiskSupport:
		"Incorrect spacing or too few supports will cause bounce and shorten deck life.",
	domain_build_guide_deckRiskGap:
		"Without a gap between boards, the deck can crown as moisture levels rise.",
	domain_build_guide_deckControlDiagonal:
		"Recheck diagonals before the final row is fixed.",
	domain_build_guide_deckControlSpacers:
		"Use spacers to keep deck board gaps even.",
	domain_build_guide_moreRows: "+ {count} more",
	domain_build_guide_foundationPlinthWarningGroundHigh:
		"{count} pier(s): the ground sits too high against the bearer — adjust excavation or pier length.",
	domain_build_guide_foundationPlinthWarningPlinthShort:
		"{count} pier(s): the chosen pier length is too short for the minimum burial depth — pick a longer pier.",
	domain_build_guide_foundationCheckpointPlinthWarning:
		"Resolve {count} pier position(s) the calculation flagged before mounting the bearers.",
	domain_build_guide_calloutPlinthWrong:
		"Casting the piers without handling ground slope leaves uneven supports and settling.",
	domain_build_guide_calloutPlinthRight:
		"Adjust excavation or pier length per flagged position so every support lands in the same plane.",
	domain_build_guide_drainagePurpose:
		"Coordinate floor falls and any pipework before the interior locks the floor framing.",
	domain_build_guide_drainagePrereq:
		"The foundation and floor frame must be set out before drain runs or falls are fixed.",
	domain_build_guide_drainageCheckpointFloorDrain:
		"Confirm the floor drain position against the floor framing.",
	domain_build_guide_drainageCheckpointGroundDrain:
		"Confirm site drainage carries water away from the sauna.",
	domain_build_guide_drainageFallbackMaterials:
		"Drainage materials have no separate phase; verify them manually in the foundation purchase list.",
	domain_build_guide_drainageCutNoSeparate:
		"No separate cut list. Instead, make sure penetrations do not clash with joists.",
	domain_build_guide_drainageRiskFloorDrain:
		"A misplaced floor drain forces a floor-frame rebuild.",
	domain_build_guide_drainageControlShower:
		"Coordinate the floor drain and falls with plumbing before panels and flooring are installed.",
	domain_build_guide_drainageControlGround:
		"Confirm water drains away from piers and the floor void.",
	domain_build_guide_framingPurpose:
		"Raise the walls in the right order so openings, loads, and fixings land correctly the first time.",
	domain_build_guide_framingPrereq:
		"The foundation must be level and the floor frame fixed before the walls are raised.",
	domain_build_guide_framingSubstepLayout: "Raise and align the wall lines",
	domain_build_guide_framingSubstepOpenings: "Lock openings and passages",
	domain_build_guide_framingSubstepBrace: "Anchor and brace the frame",
	domain_build_guide_framingCheckpointOpenings:
		"Verify {count} openings against the drawing and cut list.",
	domain_build_guide_framingCheckpointSquare:
		"Measure diagonals once the walls are up before they are permanently locked.",
	domain_build_guide_framingCheckpointPartitionSupport:
		"The partition lands between side-wall studs and needs {count} blocking pieces in the exterior walls before it is locked in.",
	domain_build_guide_framingMaterialPartitionSupport:
		"Add {count} blocking pieces to complete the partition tie-in.",
	domain_build_guide_framingFallbackMaterials:
		"Confirm studs, sill plates, and headers against the purchase list.",
	domain_build_guide_framingFallbackCuts:
		"Check the cut list for studs and opening members.",
	domain_build_guide_framingRiskOpenings:
		"Misplaced openings cause knock-on errors in cladding, glazing, and interior fitout.",
	domain_build_guide_framingRiskPartition:
		"If blocking is omitted, the partition gets weak side anchoring into the exterior walls.",
	domain_build_guide_framingControlBrace:
		"Brace the walls temporarily until every opening dimension is verified.",
	domain_build_guide_framingControlPartition:
		"Fix the partition edge studs into framing or blocking, not only into the panel lining.",
	domain_build_guide_crewReasonWallFrames:
		"Two people keep the wall lines true while the frame is lifted and braced.",
	domain_build_guide_crewReasonRoofFrames:
		"Roof joists and supports are safer to align when one person holds the load while the other fixes it.",
	domain_build_guide_crewReasonRoofLift:
		"Sheeting, membrane, and edge trims go on straighter and safer when someone keeps the piece in position.",
	domain_build_guide_crewReasonBarrelBands:
		"Barrel bands need even tension while another person keeps the staves aligned.",
	domain_build_guide_crewReasonBarrelStaves:
		"Staves seat more evenly when one person drives them home and the other keeps the shell round.",
	domain_build_guide_crewReasonBarrelEnds:
		"End caps are easier to seat cleanly when one person holds alignment and the other fastens the opening details.",
	domain_build_guide_crewReasonGlassDoor:
		"A glass door and frame need another set of hands so the unit is not twisted or dropped during fitting.",
	domain_build_guide_crewReasonFoil:
		"Foil seams stay tighter when one person stretches the foil and the other staples and tapes.",
	domain_build_guide_crewReasonHeaterLift:
		"Heaters, fireboards, and hearth plates are safer to lift and align with two people.",
	domain_build_guide_barrelShellTitle: "Cradles and stave shell",
	domain_build_guide_barrelShellPurpose:
		"Level the cradles first, then build the shell stave by stave so the barrel stays round from the start.",
	domain_build_guide_barrelShellPrereq:
		"The foundation beams must be level before any cradle or stave is fixed.",
	domain_build_guide_barrelShellSubstepCradles:
		"Level the cradles and barrel line",
	domain_build_guide_barrelShellSubstepStaves:
		"Drive in the staves with a rubber mallet",
	domain_build_guide_barrelShellCheckpointCradlesLevel:
		"Shim and level every cradle so the barrel centerline stays straight from front to back.",
	domain_build_guide_barrelShellCheckpointCradlesSpacing:
		"Lock cradle spacing before the first stave is fixed so the shell does not corkscrew later.",
	domain_build_guide_barrelShellCheckpointStavesStarter:
		"Start from the reference stave and confirm the groove/tongue engagement before you accelerate the sequence.",
	domain_build_guide_barrelShellCheckpointStavesSequence:
		"Seat staves progressively with a rubber mallet and keep the shell round as each course closes.",
	domain_build_guide_barrelShellCradleFallback:
		"Have the cradle supports ready before the stave shell starts.",
	domain_build_guide_barrelShellStavesFallback:
		"Have the barrel staves counted and sorted before shell assembly starts.",
	domain_build_guide_barrelShellCutsFallback:
		"Check the cut list for cradle profiles and stave lengths before the shell closes.",
	domain_build_guide_barrelShellRiskTwist:
		"If the cradles are out of level or the staves are driven unevenly, the barrel twists and the end caps stop fitting cleanly.",
	domain_build_guide_barrelShellControlReference:
		"Keep one centerline and check roundness often instead of trying to correct the shell after several staves.",
	domain_build_indoorCeilingTitle:
		"Ceiling joists",
	domain_build_indoorOuterTitle:
		"Outside facing the room",
	domain_build_guide_indoorCeilingPurpose:
		"Carries the sauna's ceiling, insulation and foil. The house above takes the snow and rain, so no slope, roof boarding or roofing felt is needed.",
	domain_build_guide_indoorCeilingPrereq:
		"The wall frames must be plumb and anchored to the floor before the joists go on the top plates.",
	domain_build_guide_indoorCeilingSubstep:
		"Lay ceiling joists on the top plates",
	domain_build_guide_indoorCeilingCheckpoint:
		"Cut the joists flush with the walls, no overhang, 600 mm centres.",
	domain_build_guide_indoorCeilingRisk:
		"The chimney passes both the sauna ceiling and the house's floor structures. Each penetration needs its own fire protection.",
	domain_build_guide_indoorCeilingControl:
		"Check that the ceiling is level before foil and panelling go up.",
	domain_build_guide_indoorOuterPurpose:
		"Free-standing walls are seen from the room and get panelling and trim straight on the studs. The door is hung here too.",
	domain_build_guide_indoorOuterPrereq:
		"The frame and ceiling are done, and walls against the room have their wood-fibre board.",
	domain_build_guide_indoorFibreboardSubstep:
		"Screw wood-fibre board to walls against an existing wall",
	domain_build_guide_indoorFibreboardWrong:
		"Raising the wall first and trying to fit the board behind it.",
	domain_build_guide_indoorFibreboardRight:
		"Screw the board to the studs while the wall is lying down. You cannot reach it afterwards.",
	domain_build_guide_indoorFibreboardCheckpoint:
		"At least 20 mm air gap between the board and the room's wall all the way.",
	domain_build_guide_indoorOuterPanelSubstep:
		"Panelling and trim on free-standing walls",
	domain_build_guide_indoorOuterPanelCheckpoint:
		"Nail the panelling straight to the studs. Indoors no wind barrier or battens are needed.",
	domain_build_guide_indoorOuterControl:
		"Check that the trim covers the gap around the door and windows.",
	domain_build_guide_indoorDrainagePrereq:
		"Measure the floor's fall and the existing floor drain before pipework is decided.",
	domain_build_guide_indoorDrainageControl:
		"Check that the floor under the sauna is dry and moisture resistant.",
	domain_build_guide_indoorFramingPrereq:
		"The existing floor must be level and the floor frame fixed before the walls are raised.",
	domain_build_guide_indoorMoisturePrereq:
		"Decide the air gap, wood-fibre board and sealed joints before the insulation is closed in. Follow one consistent solution for an indoor sauna.",
	domain_build_guide_kickoffSummaryNoFoundation:
		"Lock the placement and critical services before you begin the step-by-step build guide.",
	domain_build_guide_roofPurpose:
		"Weather-tighten the shell early so framing and insulation are not left exposed to rain.",
	domain_build_guide_roofPrereq:
		"Wall framing and roof supports must be locked and plumb before sheathing or membranes are installed.",
	domain_build_guide_roofSubstepJoists: "Install roof joists and supports",
	domain_build_guide_roofSubstepGables: "Build gables and infill",
	domain_build_guide_roofSubstepWeather: "Seal the roof weather-tight",
	domain_build_guide_roofCheckpointJoists:
		"Confirm roof joist {dimension} at {spacing} mm centers.",
	domain_build_guide_roofCheckpointJoistsFallback:
		"Confirm roof joists and spacing against the drawing.",
	domain_build_guide_roofCheckpointGableInfill:
		"Install the two gable trusses on the front/back top plates and fill the gable triangle with infill studs before exterior cladding closes the wall.",
	domain_build_guide_roofCheckpointWeather: "Roof build-up: {layers}.",
	domain_build_guide_roofCheckpointWeatherFallback:
		"Membrane and fascia trim must be installed before exterior cladding closes the structure.",
	domain_build_guide_roofMaterialsLayers: "Roof finish: {layers}.",
	domain_build_guide_roofMaterialsAccessories: "Flashings: {accessories}.",
	domain_build_guide_roofFallbackMaterials:
		"Confirm sheathing, battens, and roof membrane against the purchase list.",
	domain_build_guide_roofFallbackCuts:
		"Check the cut list for roof joists and roof decking.",
	domain_build_guide_roofRiskMoisture:
		"An open eave edge or unfinished membrane traps moisture in the walls and insulation.",
	domain_build_guide_roofControlFinishWeather:
		"Finish the weatherproofing before exterior cladding or interior work starts.",
	domain_build_guide_roofControlAccessories:
		"Verify these accessories are included: {accessories}.",
	domain_build_guide_roofBarrelMaterialBands:
		"Have the barrel bands and related hardware ready before the final tightening pass.",
	domain_build_guide_barrelRoofTitle: "Arch weatherproofing",
	domain_build_guide_barrelRoofPurpose:
		"Protect the curved shell with the planned roof layers before interior moisture or weather can reach the stave package.",
	domain_build_guide_barrelRoofPrereq:
		"The stave shell and end caps must be locked before the arch covering goes on.",
	domain_build_guide_barrelRoofSubstepWeather: "Wrap and seal the barrel arch",
	domain_build_guide_barrelRoofCheckpointWeather:
		"Apply the curved roof build-up over the arch: {layers}.",
	domain_build_guide_barrelRoofCheckpointWeatherFallback:
		"Finish the curved weatherproofing before the shell is left exposed overnight.",
	domain_build_guide_barrelRoofFallbackMaterials:
		"Confirm the curved roof covering and edge sealing materials against the purchase list.",
	domain_build_guide_barrelRoofCutsFallback:
		"Check the cut list for stave trimming before the roof covering is locked.",
	domain_build_guide_barrelRoofRiskWeather:
		"A partly sealed arch quickly lets water into the stave shell and makes later band tensioning harder.",
	domain_build_guide_barrelRoofControlWeather:
		"Finish one complete weatherproof arch layer at a time so no part of the barrel is left half sealed.",
	domain_build_guide_facadePurpose:
		"Build a ventilated facade that handles weather and lets the structure dry after use.",
	domain_build_guide_facadePrereq:
		"The roof must be weather-tight before the air gap and exterior cladding go up.",
	domain_build_guide_facadeSubstepCladding:
		"Install cross-battens (standing spacer battens + horizontal nailing battens) and cladding",
	domain_build_guide_facadeSubstepDoor:
		"Hang the door and lock the opening details",
	domain_build_guide_facadeCheckpointAirGap:
		"Keep the ventilation gap and base edge open so the facade can dry out.",
	domain_build_guide_facadeCheckpointOpeningsVent:
		"Check that door and window trims do not block ventilation around the openings.",
	domain_build_guide_facadeCheckpointDoorSafety:
		"Confirm the sauna door opens outward, has no lock/latch, and uses a wooden handle inside.",
	domain_build_guide_facadeFallbackMaterials:
		"Confirm cladding, wind barrier, and battens against the purchase list.",
	domain_build_guide_facadeFallbackCuts:
		"Check the cut list for cladding and any trim pieces.",
	domain_build_guide_facadeRiskMoisture:
		"A closed base edge or poor opening details will cause moisture problems.",
	domain_build_guide_facadeControlVentilated:
		"Keep the pier foundation ventilated even after the facade is complete.",
	domain_build_guide_barrelEndsTitle: "End caps and openings",
	domain_build_guide_barrelEndsPurpose:
		"Fit the round end caps and opening details cleanly before the bands are fully tensioned.",
	domain_build_guide_barrelEndsPrereq:
		"The stave shell must hold its round shape before the front and back end caps are fixed.",
	domain_build_guide_barrelEndsSubstepCaps: "Seat end caps, door, and vents",
	domain_build_guide_barrelEndsCheckpointCaps:
		"Dry-fit both end caps and confirm the shell stays round all the way around each opening.",
	domain_build_guide_barrelEndsCheckpointDoor:
		"Align the door opening and ventilation cutouts before the surrounding fixings are fully tightened.",
	domain_build_guide_barrelEndsFallbackMaterials:
		"Confirm end caps and door package before the front opening is closed.",
	domain_build_guide_barrelEndsFallbackCuts:
		"Check the cut list for end caps before the circular edges are fixed permanently.",
	domain_build_guide_barrelEndsRiskRound:
		"If the end caps are forced into an oval shell, door fit and later band tension both drift out of tolerance.",
	domain_build_guide_barrelEndsControlMeasure:
		"Measure across the shell in two directions while fitting the end caps so roundness is held, not guessed.",
	domain_build_guide_insulationPurpose:
		"Lock heat and moisture control into the walls and roof before the heater and interior fitout are installed.",
	domain_build_guide_insulationPrereq:
		"The outer shell must be weather-tight before insulation and interior sauna cladding are installed.",
	domain_build_guide_insulationSubstepInsulation: "Fill wall and roof cavities",
	domain_build_guide_insulationSubstepFoil: "Seal the sauna foil layer",
	domain_build_guide_insulationSubstepPanel:
		"Install battens, paneling, and crown trim",
	domain_build_guide_insulationCheckpointFill:
		"Fill wall and roof cavities without gaps around openings or roof junctions.",
	domain_build_guide_insulationCheckpointFoil:
		"Install sauna foil / aluminium in the hot zone before the paneling goes up.",
	domain_build_guide_insulationCheckpointBattens:
		"Install ceiling battens below the roof joists so the ceiling gets a service gap between the foil and the sauna panel.",
	domain_build_guide_insulationFallbackMaterials:
		"Confirm insulation, foil, and paneling against the purchase list.",
	domain_build_guide_insulationFallbackCuts:
		"No separate cut list exists for wall paneling, ceiling paneling, and trim; verify panel layout and waste manually.",
	domain_build_guide_insulationRiskBarrier:
		"The wrong vapor barrier or a leaky foil layer causes condensation and heat loss.",
	domain_build_guide_insulationRiskBattens:
		"If the ceiling panel is fixed directly to the roof joists without battens, the ventilation gap disappears and the foil is hard to keep airtight.",
	domain_build_guide_insulationControlPhoto:
		"Photograph penetrations and foil seams before the paneling covers them.",
	domain_build_guide_insulationControlSequence:
		"Lock the sequence insulation -> sauna foil -> ceiling battens -> sauna panel / trim in the instructions, BOM, and cut list.",
	domain_build_guide_insulationFoilMaterialFallback:
		"Have the sauna foil, aluminium tape, and penetration tape ready before the seams are closed.",
	domain_build_guide_insulationPanelMaterialFallback:
		"Have the paneling, battens, and concealed fixings ready before the interior surface is closed.",
	domain_build_guide_barrelBandsTitle: "Steel bands and settling",
	domain_build_guide_barrelBandsPurpose:
		"Tension the steel bands evenly, then plan an early retightening pass after the heated timber has settled.",
	domain_build_guide_barrelBandsPrereq:
		"The shell, end caps, and weatherproof arch should be in place before final band tensioning starts.",
	domain_build_guide_barrelBandsSubstepTension:
		"Tension the steel bands evenly",
	domain_build_guide_barrelBandsSubstepRetighten:
		"Schedule the first retightening pass",
	domain_build_guide_barrelBandsCheckpointEven:
		"Tighten every steel band progressively around the shell instead of fully locking one band at a time.",
	domain_build_guide_barrelBandsCheckpointRetighten:
		"Retighten the bands after roughly 2-4 weeks of settling and first heat cycles.",
	domain_build_guide_barrelBandsFallbackMaterials:
		"Have the steel bands and fastening hardware ready before the final tightening sequence starts.",
	domain_build_guide_barrelBandsFallbackCuts:
		"Check the cut list for steel-band lengths and overlap before tensioning begins.",
	domain_build_guide_barrelBandsRiskTension:
		"Uneven band tension can flatten the shell, loosen the end caps, or leave leaks after the timber dries.",
	domain_build_guide_barrelBandsControlSequence:
		"Work around the barrel in small tightening increments so the shell stays round while the bands come up to load.",
	domain_build_guide_barrelBandsControlRetighten:
		"Write the retightening date into the handover checklist so it is not forgotten after delivery.",
	domain_build_guide_partitionSubstepTitle:
		"Frame and anchor the partition wall",
	domain_build_guide_partitionFallbackMaterials:
		"Confirm partition studs and blocking before the room split is closed.",
	domain_build_guide_partitionCheckpointBlocking:
		"Install {count} blocking pieces where the partition lands before the room split is fixed.",
	domain_build_guide_partitionCheckpointAnchor:
		"Fasten the partition edge into framing or blocking on both side walls before paneling hides the joint.",
	domain_build_guide_partitionCheckpointDirectAnchor:
		"Anchor the partition directly into the aligned studs on both side walls before paneling closes the split.",
	domain_build_guide_glassWallSubstepTitle: "Install the panorama glass wall",
	domain_build_guide_glassWallCrewReason:
		"Glass panes are heavy and fragile — lift and align them with two people.",
	domain_build_guide_glassWallCalloutWrong:
		"Wedging or screwing straight against the glass, or lifting large panes solo.",
	domain_build_guide_glassWallCalloutRight:
		"Set the glass on setting blocks in the header, let it float, and seal flexibly all around — always carry panes with two people and suction cups.",
	domain_build_guide_glassWallMaterialFallback:
		"Tempered safety glass, setting blocks, EPDM gasket, and flexible sealant for the panorama wall.",
	domain_build_guide_glassWallCheckpointOpening:
		"Check that the header and mullion are plumb and level before setting the glass.",
	domain_build_guide_glassWallCheckpointSafetyGlass:
		"Use tempered safety glass on {count} wall(s) — the glass area reaches {threshold} m² or more.",
	domain_build_guide_glassWallCheckpointGlassSpec:
		"Confirm the glass spec against the permit paperwork before ordering.",
	domain_build_guide_glassWallCheckpointSeal:
		"Seal tightly around the whole pane so water drains outward, not into the frame.",
	domain_build_guide_relaxInsulationCheckpoint:
		"Insulate and foil only the sauna room — the relax room does not get sauna insulation.",
	domain_build_guide_relaxVentCheckpoint:
		"Ventilate each room on its own: the sauna room with intake and exhaust, the relax room with its own air change.",
	domain_build_guide_relaxBenchCheckpoint:
		"Build the relax-room bench separately from the sauna benches — it meets different height and comfort needs.",
	domain_build_guide_benchesPurpose:
		"Build ergonomics and safety into the sauna before the heater and final inspection take over.",
	domain_build_guide_benchesPrereq:
		"Plan blocking behind the paneling for the bench supports. Fixings must reach structural studs or blocking; paneling alone is not a suitable fixing base.",
	domain_build_guide_benchesCheckpointHeights:
		"Upper bench about {upper} mm and lower bench about {lower} mm.",
	domain_build_guide_benchesCheckpointHeightsFallback:
		"Confirm bench heights and walking clearances before fixing anything.",
	domain_build_guide_benchesCheckpointFinish:
		"Round off exposed edges and check concealed fixings where the body touches the wood.",
	domain_build_guide_benchesFallbackMaterials:
		"Confirm bench timber and fixings against the purchase list.",
	domain_build_guide_benchesFallbackCuts:
		"Check the cut list for benches manually before cutting premium timber.",
	domain_build_guide_benchesRiskComfort:
		"Wrong bench heights or hard edges reduce both safety and sauna comfort.",
	domain_build_guide_benchesControlTrialFit:
		"Dry-fit the bench on its supports. For a lower bench on end-wall cleats, the removable frame leaves 10 mm clearance at each end wall; its long rails must bear directly on the cleats. Prevent movement with the selected system's stop blocks or removable fixings. Check the span, frame, solid packing and wall-screw anchorage in studs or blocking.",
	domain_build_guide_benchesMaterialFallback:
		"Prepare knot-free bench timber in aspen, alder, or abachi plus concealed fixings before installation.",
	domain_build_guide_benchesMeasurementHeights:
		"Ergonomics: upper bench {upper} mm and lower bench {lower} mm from the floor.",
	domain_build_guide_benchesSubstepFrame: "Bench frame & heights",
	domain_build_guide_benchesSubstepSeat: "Seat & finish",
	domain_build_guide_benchesSubstepHatch: "Hatch & access",
	domain_build_guide_benchesCheckpointHatch:
		"Build a removable seat hatch or section for access to the heater and cleaning.",
	domain_build_guide_crewReasonBenchFrame:
		"Two people set the bench frame level on its supports and secure the fixings.",
	domain_build_guide_systemsPurposeWood:
		"Prepare stove placement, ventilation, and fire clearances before the chimney completes the heating system.",
	domain_build_guide_systemsPurposeElectric:
		"Coordinate the electric heater, ventilation, and safe power connection before final handover.",
	domain_build_guide_systemsPrereq:
		"Interior paneling and benches must be complete so the heater's real position can be locked in.",
	domain_build_guide_systemsSubstepPower:
		"Lock the heater, cable run, and clearances",
	domain_build_guide_systemsSubstepVent: "Cut in supply and exhaust air",
	domain_build_guide_systemsCheckpointElectric:
		"Confirm the RCD, fuse protection, and cable routing with an electrician before energizing.",
	domain_build_guide_systemsCheckpointWood:
		"Confirm stove position, shields, and fresh-air intake before the chimney is installed.",
	domain_build_guide_systemsCheckpointVent:
		"Ventilation: intake {intake} mm and exhaust {exhaust} mm.",
	domain_build_guide_systemsCheckpointVentFallback:
		"Confirm supply and exhaust air per the ventilation strategy.",
	domain_build_guide_systemsFallbackMaterials:
		"Confirm the heater, cable, or ventilation parts against the purchase list.",
	domain_build_guide_systemsPowerMaterialFallback:
		"Prepare cable, penetrations, and heat-resistant parts to match the electrical spec before the heater is lifted in.",
	domain_build_guide_systemsVentMaterialFallback:
		"Prepare vent grilles and duct parts with at least {minArea} cm² free area per vent.",
	domain_build_guide_systemsCutElectric:
		"No typical cut list. Coordinate cable routes and penetrations before the electrician connects power.",
	domain_build_guide_systemsCutWood:
		"Verify vent penetrations and stove clearances before closing this stage.",
	domain_build_guide_systemsRiskElectric:
		"An electric heater must not be commissioned without a licensed connection.",
	domain_build_guide_systemsRiskFan:
		"The ventilation system requires a fan and must be test-run before final fit-out.",
	domain_build_guide_systemsControlCableSpec:
		"Electrical spec: {cableType}, {fuseLabel}, {rcdLabel}.",
	domain_build_guide_systemsControlElectric:
		"Document the electrical work and leave service access around the heater.",
	domain_build_guide_systemsControlWood:
		"Verify clearances around the stove and wall shields before the first firing.",
	domain_build_guide_systemsMeasurementCable:
		"Electrical spec: {cableType}, {fuseLabel}, around {totalCableLength} m total cable length.",
	domain_build_guide_systemsMeasurementVent:
		"Ventilation: intake {intake} mm, exhaust {exhaust} mm, and at least {minArea} cm² free area per vent.",
	domain_build_guide_systemsMeasurementHotZone:
		"{count} electrical component(s) in the hot zone need 180 °C silicone cable (SiHF) — ordinary PVC melts.",
	domain_build_guide_systemsCheckpointHotZone:
		"Confirm {count} hot-zone component(s) are wired with heat-resistant silicone cable.",
	domain_build_guide_calloutHeaterRightUnavailable:
		"Clearance figures are not calculated for this sauna type — follow the heater's installation manual for minimum clearances.",
	domain_build_guide_chimneyPurpose:
		"Complete the heating system with the correct penetration, fire clearances, and inspection.",
	domain_build_guide_chimneyPrereq:
		"Stove position and roof penetration must be locked before the chimney is installed.",
	domain_build_guide_chimneyCheckpointClearance:
		"Follow the clearance rule: {rule}",
	domain_build_guide_chimneyCheckpointBay:
		"Lock the chimney to a free roof bay before cutting. The chimney must not cut through a joist or roof member.",
	domain_build_guide_chimneyCheckpointCeilingFlange:
		"Fix the ceiling flange to the finished sauna ceiling, not hanging free in space.",
	domain_build_guide_chimneyCheckpointRoofPenetration:
		"Install the roof flashing and seal to the finished outer roof. Keep at least 100 mm clear between the chimney pipe and all combustible roof members. Fill the gap with non-combustible mineral wool (class A1).",
	domain_build_guide_chimneyCheckpointStormCollar:
		"Install a storm collar / rain collar around the pipe above the flashing to stop water ingress.",
	domain_build_guide_chimneyCheckpointCap:
		"Install a rain cap at the top of the chimney to protect against rain and snow.",
	domain_build_guide_chimneyCheckpointSweeper:
		"Book the chimney sweep / installer before the first firing.",
	domain_build_guide_chimneyMaterialsSystem:
		"{system} Ø{diameter} mm, height {height} m.",
	domain_build_guide_chimneyCutCenter:
		"Measure both the inner ceiling center and the outer roof center one more time before cutting panels, membrane, or sheet metal.",
	domain_build_guide_chimneyCutMargin:
		"Mark the roof cutout with a 100 mm margin around the pipe outer diameter (BBR 5:42).",
	domain_build_guide_chimneyRiskBeam:
		"If the chimney lines up with a roof joist, move the stove / chimney to the next clear bay instead of cutting the joist.",
	domain_build_guide_chimneyRiskFireSeal:
		"Incorrect fire sealing at the roof penetration can cause a fire. At least 100 mm of fire-rated mineral wool (A1) is required around the pipe through the roof (BBR 5:42).",
	domain_build_guide_chimneyRiskStormCollar:
		"A missing storm collar leads to water ingress in the roof structure.",
	domain_build_guide_chimneyControlClearance:
		"Verify that all combustible materials keep at least the CE/manual clearance from the chimney surface, minimum 100 mm in this standard setup.",
	domain_build_guide_chimneyControlAnchors:
		"Fix the inner ceiling plate to the finished ceiling and the roof flashing to the outer roof; they are two separate fixing points.",
	domain_build_guide_chimneyControlStormCollar:
		"Check that the storm collar seals tightly to the flashing without gaps.",
	domain_build_guide_chimneyControlCap:
		"Check that the rain cap sits firmly and that water cannot enter the flue.",
	domain_build_guide_chimneyControlDocuments:
		"Document the installation and keep the inspection records from the chimney sweep.",
	domain_build_guide_chimneyMeasurementClearance:
		"Clearance and inspection rule: {rule}",
	domain_build_guide_calloutFoilWrong:
		"Do not flip the sauna foil the wrong way, use regular building plastic in the hot zone, or stack two vapor barriers in the same wall.",
	domain_build_guide_calloutFoilRight:
		"Install one sauna foil layer on the warm side with the shiny face toward the sauna and tape every seam with aluminium tape.",
	domain_build_guide_calloutPanelWrong:
		"Do not fix paneling straight against the foil, leave it too close to the floor, or build with gypsum, chipboard, or OSB behind sauna paneling.",
	domain_build_guide_calloutPanelRight:
		"Lock a 12–28 mm service gap, keep at least 70 mm clear above the floor, and use concealed fixings where skin meets paneling or benches.",
	domain_build_guide_calloutDoorWrong:
		"Do not fit a door that opens inward, binds from heat, or can be locked from outside.",
	domain_build_guide_calloutDoorRight:
		"Let the sauna door open outward, skip the lock/latch, and use a wooden handle on the inside.",
	domain_build_guide_calloutBenchesWrong:
		"Do not use knotty stock, run screw heads through the seat surface, or close the slat gaps too tightly.",
	domain_build_guide_calloutBenchesRight:
		"Build in aspen, alder, or abachi, keep about a 15 mm gap between slats, and screw from underneath where possible.",
	domain_build_guide_calloutVentWrong:
		"Do not make the intake closable, undersize the vent area, or put both intake and exhaust high on the same wall.",
	domain_build_guide_calloutVentRight:
		"Give each vent at least {minArea} cm² free area, keep the intake open by the heater, and place the exhaust high on the diagonal opposite wall.",
	domain_build_guide_calloutHeaterWrong:
		"Do not push the heater or stove tight to combustible material or let the door swing cross a hot protection zone.",
	domain_build_guide_calloutHeaterRight:
		"Lock the heater with at least {side} mm side, {front} mm front, and {ceiling} mm ceiling clearance from the calculation, ideally on the same wall as the door.",
	domain_build_guide_calloutHeaterRightWood:
		"Keep the stove and door on the same wall when the layout allows it and follow the manual clearance rule: {rule}.",
	domain_build_guide_calloutChimneyWrong:
		"Do not light the stove before final approval and do not run the chimney through combustible members without a clear fire-safe passage.",
	domain_build_guide_calloutChimneyRight:
		"Keep the clearance rule {rule}, use approved fire sealing, and book the chimney sweep's final inspection before the first fire.",
	domain_build_guide_handoverPurpose:
		"Finish the project with safety checks, documentation, and the first supervised test run.",
	domain_build_guide_handoverPrereqWood:
		"The chimney and fire clearances must be approved before the first firing.",
	domain_build_guide_handoverPrereqElectric:
		"The electric heater must be connected and function-tested by the right qualified person.",
	domain_build_guide_handoverCheckpointWood:
		"Keep the chimney-sweep report and perform the first firing under supervision.",
	domain_build_guide_handoverCheckpointElectric:
		"Function-test the heater, ventilation, and door before the first sauna session.",
	domain_build_guide_handoverSubstepSelfCheck: "Self-inspection & sign-off",
	domain_build_guide_handoverSubstepDocumentation:
		"Documentation & certificates",
	domain_build_guide_handoverSubstepFirstBath: "Break-in & first bath",
	domain_build_guide_handoverCheckpointDocPhotos:
		"Collect photos of hidden installations (foil, cabling, ventilation) for future maintenance.",
	domain_build_guide_handoverCheckpointDocElectric:
		"Archive the electrical installation certificate and the heater's test report.",
	domain_build_guide_handoverCheckpointDocWood:
		"Archive the sweep's inspection report and the chimney documentation.",
	domain_build_guide_handoverCheckpointFirstCure:
		"Run the heater hot without bathing the first time to burn off manufacturing residue and odour.",
	domain_build_guide_handoverCheckpointFirstBath:
		"Throw a little löyly and check draft, temperature, and that nothing smells burnt before the first real bath.",
	domain_build_guide_calloutFirstBathWrong:
		"Bathing before the heater is burned in gives an unpleasant smell and can discolour the panelling.",
	domain_build_guide_calloutFirstBathRight:
		"Run it dry first, air it out, and take the first bath once the smell is gone.",
	domain_build_guide_handoverMaterialNone:
		"No new material phase. Make sure all installed materials match the PDF and purchase list.",
	domain_build_guide_handoverCutNone:
		"No new cut list. Instead, gather final checks, photos, and installation evidence.",
	domain_build_guide_handoverRiskLate:
		"Missed final checks usually show up only during operation and are more expensive to fix later.",
	domain_build_gate_electricianTitle: "Electrician gate before insulation",
	domain_build_gate_electricianPurpose:
		"Book and lock the fixed electrical rough-in before insulation and sauna foil close the wall.",
	domain_build_gate_electricianLeadTime:
		"Book the electrician {min}–{max} weeks before insulation and sauna foil.",
	domain_build_gate_electricianChecklistFeed:
		"Confirm the supply cable to the building and the breaker/RCD plan for the heater load.",
	domain_build_gate_electricianChecklistHeaterStub:
		"Confirm the connection point roughly 30 cm above the floor behind the heater before the wall is closed.",
	domain_build_gate_electricianChecklistConduit:
		"Run conduit between the insulation and the exterior cladding, never in the hot foil zone behind the sauna lining.",
	domain_build_gate_plumbingTitle: "Plumbing gate for shower and floor drain",
	domain_build_gate_plumbingPurpose:
		"Coordinate the floor drain, falls, and pipe routing with plumbing before the floor deck is locked.",
	domain_build_gate_plumbingLeadTime:
		"Book plumbing {min}–{max} weeks before the drainage and floor drain are built in.",
	domain_build_gate_fireplaceNotificationTitle:
		"Fireplace notice at project start",
	domain_build_gate_fireplaceNotificationPurpose:
		"Submit the fireplace notice immediately so the municipality lead time can run while you build.",
	domain_build_gate_fireplaceNotificationLeadTime:
		"Allow {min}–{max} weeks for the fireplace notice and start-clearance handling.",
	domain_build_gate_fireplaceNotificationChecklistSubmit:
		"Submit the fireplace notice with heater, chimney, and placement documents before the build pace increases.",
	domain_build_gate_fireplaceStartTitle:
		"Wait for start clearance before wood-heater install",
	domain_build_gate_fireplaceStartPurpose:
		"Do not install the wood heater or chimney until the fireplace start clearance has been issued.",
	domain_build_gate_fireplaceStartLeadTime:
		"Installation starts only after the fireplace start clearance is issued.",
	domain_build_gate_fireplaceStartChecklistWait:
		"Wait for the start clearance before mounting the wood heater, heat shields, or chimney.",
	domain_build_gate_fireplaceStartChecklistClearanceFallback:
		"Verify the fire clearances and roof-penetration details against the selected chimney before installation.",
	domain_build_gate_fireplaceStartChecklistDocuments:
		"Keep the approval, drawing set, and product documents available on site when installation starts.",
	domain_build_gate_fireplaceInspectionTitle:
		"Chimney inspection before handover",
	domain_build_gate_fireplaceInspectionPurpose:
		"Book the installation inspection and collect the approval record before handover and first firing.",
	domain_build_gate_fireplaceInspectionLeadTime:
		"Book the chimney sweep / inspector before handover and final checks.",
	domain_build_gate_fireplaceInspectionChecklistBooking:
		"Book the installation inspection with the chimney sweep or inspector before final checks.",
	domain_build_gate_fireplaceInspectionChecklistProtocol:
		"Keep the approved inspection report together with the rest of the build documentation.",
	domain_build_gate_fireplaceInspectionChecklistNoFire:
		"Never light the heater before final approval and a passed installation inspection.",
	domain_build_timeline_weekendPassSingle: "{count} weekend session",
	domain_build_timeline_weekendPassPlural: "{count} weekend sessions",
	domain_build_timeline_weekendRangeSingle: "{count} weekend",
	domain_build_timeline_weekendRangePlural: "{count} weekends",
	domain_build_timeline_weekendRangeBetween: "{min}–{max} weekends",
	domain_build_timeline_leadRangeSingle: "{count} week of waiting time",
	domain_build_timeline_leadRangePlural: "{count} weeks of waiting time",
	domain_build_timeline_leadRangeBetween: "{min}–{max} weeks of waiting time",
	domain_build_timeline_calendarEstimateNoLead:
		"~{hours} h of work ≈ {weekendRange}",
	domain_build_timeline_calendarEstimateWithLead:
		"~{hours} h of work ≈ {weekendRange} + {leadRange}",
	domain_build_timeline_weatherCriticalReason:
		"Reach this point before rain; the frame should not stay open once the weather turns.",

	/* ── Domain: Material Phases ─────────────────────────── */
	domain_phase_foundation: "Foundation & piers",
	domain_phase_deck: "Deck & terrace",
	domain_phase_framing: "Framing & studs",
	domain_phase_roofExterior: "Roof & exterior",
	domain_phase_interior: "Interior & benches",
	domain_phase_systems: "Heater & systems",

	/* ── Domain: Chimney ─────────────────────────────────── */
	domain_chimney_kit:
		"Sauna chimney 1.5 m, Ø220 (Ø115 smoke pipe, rain cap, rain collar, lead-through flange)",
	domain_chimney_extensionLong: "Sauna chimney extension 1.0 m",
	domain_chimney_extensionShort: "Sauna chimney extension 0.5 m",
	domain_chimney_trimmerHeaders: "Chimney trimmer headers",
	domain_chimney_trimmerNoggins: "Chimney trimmer noggins (ceiling battens)",
	domain_chimney_dimension: "Chimney",
	domain_chimney_recommendedSystem:
		"Insulated steel sauna chimney (Harvia WHP1500 system), Ø115 smoke pipe to a Ø220 chimney",
	domain_chimney_clearanceRule:
		"At least 100 mm from the chimney casing and 500 mm from the uninsulated smoke pipe to combustible material.",
	domain_chimney_maintenanceNote:
		"Swept from the top: the rain cap lifts off, so plan safe access to the roof.",
	domain_chimney_fireSealInsulation:
		"Fire seal roof penetration (mineral wool A1)",
	domain_chimney_throughHouse:
		"Runs through the house: {m} m from the sauna ceiling to the ridge, plus 1 m above the ridge. Every floor structure the chimney passes needs its own fire-rated penetration.",
	domain_chimney_throughHouseAssumed:
		"The house height is not given, so we assume {m} m from the sauna ceiling to the ridge plus 1 m above it. Enter it under The room the sauna stands in. Every floor structure the chimney passes needs its own fire-rated penetration.",
	domain_chimney_overMaxHeight:
		"The chimney runs {m} m from the stove, taller than the sauna chimney's 5 m maximum. Through a house you need a house chimney; check with your chimney sweep before buying.",

	/* ── Domain: Drainage ────────────────────────────────── */
	domain_drain_slopeShower:
		"Floor slope 1:100 towards floor drain or drainage point.",
	domain_drain_slopeNoShower:
		"Ensure floor and ground have a slight slope so splash water can dry out.",
	domain_drain_pipeShower:
		"Minimum DN50 pipe to frost-free depth with inspection access.",
	domain_drain_pipeNoShower:
		"Gravel bed under the foundation and an opening in the sill is usually sufficient for a dry sauna.",
	domain_drain_adviceShower1:
		"Plan a gravel bed under the foundation for reliable drainage.",
	domain_drain_adviceShower2:
		"Secure plumbing connection or soakaway before closing the floor framing.",
	domain_drain_adviceNoShower1:
		"Install a capillary-break layer under the sauna.",
	domain_drain_adviceNoShower2:
		"Avoid placing the sauna in a low spot where water collects.",
	domain_drain_instrShower:
		"Wet area: plan floor drain, slope and plumbing before building the floor frame.",
	domain_drain_instrNoShower:
		"Dry sauna: focus on ground drainage and good airflow under the floor.",
	domain_drain_indoorInstrNoShower: "Indoor: no ground drainage, but the floor under the sauna must tolerate water.",
	domain_drain_indoorAdviceFloor: "Do not build on parquet or carpet. If the room has a floor drain, the sauna floor can slope towards it.",
	domain_drain_indoorPipeNoShower: "No ground drainage is needed indoors.",
	domain_drain_indoorSlopeNoShower: "The sauna stands on the existing floor. The floor must tolerate water, for example tiles or concrete.",
	domain_drain_indoorInstrShower: "Indoor with shower: plan the floor drain and waterproofing in the existing room.",
	domain_drain_indoorAdviceWetRoom: "Build the shower area as a wet room to local waterproofing standards, with a qualified installer.",
	domain_drain_indoorPipeShower: "Connect the shower to the house's drainage through a floor drain in the wet room.",

	/* ── Domain: Permit ──────────────────────────────────── */
	/* The legacy tier keys below drive the generic intl path. The
	   domain_permit_se_* keys are only rendered for the se market
	   (Swedish PBL reform, 1 December 2025) and exist here for key parity. */
	domain_permit_friggebod: "Exempt structure",
	domain_permit_attefall: "Building notification",
	domain_permit_bygglov: "Building permit",
	domain_permit_komplementbyggnad: "Permit-exempt outbuilding",
	domain_permit_komplementbyggnadOutsideDetaljplan:
		"Permit-exempt outside zoned areas only",
	domain_permit_msgUnder15:
		"Under 15 m² the project is normally classified as permit-exempt.",
	domain_permit_msg15to30:
		"15–30 m² normally requires a building notification.",
	domain_permit_msgOver30:
		"Over 30 m² normally requires a full building permit.",
	domain_permit_msgBoundary:
		"Place the sauna at least 4.5 m from the property boundary without written neighbour consent.",
	domain_permit_msgFireplace:
		"A wood-burning stove requires a separate fireplace notification and fire-safety inspection.",
	domain_permit_indoorCheckStructure: "Changes to the house's structure, fire protection or ventilation may need approval from your local building authority.",
	domain_permit_indoorMsg: "A sauna built as a room inside an existing house is not a building of its own, so outbuilding area limits and boundary distances do not apply to it.",
	domain_permit_indoorLabel: "Not applicable – sauna inside an existing building",
	domain_permit_se_msgExempt:
		"Up to {maxDetaljplan} m² the sauna is a permit-exempt outbuilding (Swedish PBL rules from 1 December 2025) – no permit, notification or start clearance is required for the building.",
	domain_permit_se_msgExemptOutsideDetaljplanOnly:
		"{maxDetaljplan}–{maxOutside} m² is permit-exempt only outside zoned (detaljplan) areas. Inside a zoned area, a building permit is required above {maxDetaljplan} m².",
	domain_permit_se_msgOverMax:
		"Above {maxOutside} m² a building permit is always required.",
	domain_permit_se_msgTotalArea:
		"The cap applies per plot: max {totalDetaljplan} m² combined permit-exempt building area inside zoned areas ({totalOutside} m² outside).",
	domain_permit_se_msgStrandskydd:
		"Within shoreline protection areas a dispensation is required even when no building permit is needed.",
	domain_permit_checkArea:
		"Check total floor area against the 15/30 m² thresholds.",
	domain_permit_checkBoundary:
		"Verify 4.5 m to the property boundary or secure neighbour consent.",
	domain_permit_checkFireplace:
		"Plan the fireplace notification and chimney sweep inspection.",
	domain_permit_checkElectrical:
		"Verify that the electrical supply can be connected per local requirements.",
	domain_permit_se_checkArea:
		"Check the building area against {maxDetaljplan} m² (inside zoned areas) or {maxOutside} m² (outside zoned areas).",
	domain_permit_se_checkTotalArea:
		"Sum all permit-exempt outbuildings on the plot against the {totalDetaljplan}/{totalOutside} m² caps.",
	domain_permit_se_checkRidgeHeight:
		"Verify that the ridge height does not exceed {maxRidge} m.",
	domain_permit_se_checkStrandskydd:
		"Check whether the plot is within shoreline protection – a dispensation is then required despite the permit exemption.",
	domain_permit_panorama_glassPercentExceeded:
		"Panorama glass on {wall} covers {percent}% of the wall. The limit is {maxPercent}%.",
	domain_permit_panorama_safetyGlassRequired:
		"Panorama glass on {wall} is {glassAreaSqm} m². Verify safety glass and product marking.",
	domain_permit_wallFront: "the front wall",
	domain_permit_wallBack: "the back wall",
	domain_permit_wallLeft: "the left wall",
	domain_permit_wallRight: "the right wall",

	/* ── Domain: Pricing ─────────────────────────────────── */
	domain_pricing_comparatorNote:
		"The market comparison adds a packaging margin for prefabrication, sales and project coordination on top of a complete DIY budget.",
	domain_pricing_allowanceDeliveryRoundingLabel:
		"Delivery, purchase rounding and waste margin",
	domain_pricing_allowanceDeliveryRoundingNote:
		"Covers delivery, rounding up to stocked lengths and the small extras that normally appear in a DIY project.",
	domain_pricing_allowanceHiddenSuppliesLabel:
		"Hidden consumables and final assembly",
	domain_pricing_allowanceHiddenSuppliesNote:
		"Covers the top-ups that still appear despite a complete BOM: extra fixings, consumables, sealing and final finish.",
	domain_pricing_allowanceProfessionalServicesLabel: "Professional steps",
	domain_pricing_allowanceProfessionalServicesNote:
		"Sums the steps where the model already flags the need for a professional, for example an electrician for the heater.",
	domain_pricing_allowanceMarketPackagingLabel:
		"Packaging margin for prefabrication, sales and coordination",
	domain_pricing_allowanceMarketPackagingNote:
		"Used only in the market comparison, to explain the difference against ready-made kits and package prices.",

	/* ── Domain: Placement ───────────────────────────────── */
	domain_place_tipElectrical:
		"Place the sauna near the existing electrical supply to minimise cable runs and groundwork.",
	domain_place_tipDrainageShower:
		"Choose a spot where drainage and plumbing can be frost-protected.",
	domain_place_tipDrainageNoShower:
		"Avoid low spots where water can pool under the foundation.",
	domain_place_tipWoodAirflow:
		"A wood-burning sauna benefits from clear airflow around the chimney and a safe distance from the house.",
	domain_place_checkBoundary:
		"☐ At least 4.5 m to property boundary or written neighbour consent.",
	domain_place_checkWoodDistance:
		"☐ At least 8 m to house or verified fire-safety distance.",
	domain_place_checkElectricDistance:
		"☐ Check distance to other buildings and choose non-combustible surroundings near existing power supply.",
	domain_place_checkDrainShower:
		"☐ Ensure frost-protected routing for drainage / plumbing.",
	domain_place_checkDrainNoShower:
		"☐ Ensure the ground slopes away from the sauna for natural drainage.",
	domain_place_indoorCheckFloor: "☐ Check that the floor tolerates water and that the room is tall enough for the sauna ceiling.",
	domain_place_indoorCheckElectric: "☐ Check that the house's main fuse can carry the heater and have an electrician run a dedicated circuit.",
	domain_place_indoorCheckWood: "☐ A wood stove indoors needs a chimney through the house and local approval. Talk to a chimney sweep before choosing wood.",
	domain_place_indoorTipVentilation: "Choose a room where the sauna can take supply air near the heater and exhaust to the room or outdoors.",

	/* ── Domain: Heater ──────────────────────────────────── */
	domain_heater_elReqWood:
		"Wood-burning sauna: plan electricity for lighting, controls and optional fan only.",
	domain_heater_elReqElectric: "400 V three-phase, {fuse} A fuse",
	domain_heater_warningUpfuse: "Warning: The main fuse may need upgrading.",
	domain_heater_warningOverLargestStep:
		"The room is equivalent to {volume} m³, but the largest heater step is {kw} kW. A heater that size will not heat it: split the room with a partition or choose a heater whose manufacturer room volume covers {volume} m³.",
	domain_heater_modelFit_undersized:
		"{model} is rated up to {maxM3} m³, but the sauna counts as {volumeM3} m³. Choose a stronger model.",
	domain_heater_modelFit_oversized:
		"{model} is made for at least {minM3} m³, but the sauna counts as {volumeM3} m³. Choose a smaller model.",

	/* ── Domain: Weight ──────────────────────────────────── */
	domain_weight_adviceLight:
		"Light load — can be transported on a standard unbraked trailer.",
	domain_weight_adviceMedium:
		"Weight exceeds 600 kg. Two trips with a standard trailer, or rent a heavy-duty trailer.",
	domain_weight_adviceHeavy:
		"Heavy load! Delivery by crane truck from the building supplier is recommended.",

	/* ── Domain: Ergonomics ──────────────────────────────── */
	domain_ergo_adviceOk:
		"Optimal bench height. The bather's feet are in the hot air zone above the heater stones.",
	domain_ergo_adviceFail:
		"Cold-draft risk! The foot bench falls below the heater stones.",
	domain_ergo_warningFail:
		"The foot bench is below the heater stones — the bather's feet will be cold. Raise the ceiling or choose a smaller heater.",

	/* ── Domain: Heater clearance ────────────────────────── */
	domain_heaterClearance_obstacle_wall: "wall",
	domain_heaterClearance_obstacle_bench: "bench",
	domain_heaterClearance_obstacle_ceiling: "ceiling",
	domain_heaterClearance_obstacle_partition: "partition wall",
	domain_heaterClearance_violation:
		"Safety clearance to {obstacle}: {actualMm} mm — at least {requiredMm} mm required (building code / manufacturer's install manual).",

	/* ── BBR: Heater clearance section ───────────────────── */
	bbr_heaterClearanceTitle: "Heater safety clearance",
	bbr_heaterClearanceIntro:
		'Minimum distance from the heater to combustible material (benches, walls, ceiling). Show the zone in the 3D view with the "Safety clearance" layer.',
	bbr_heaterClearanceSide: "Side (min)",
	bbr_heaterClearanceFront: "Front (min)",
	bbr_heaterClearanceCeiling: "Ceiling (min)",
	bbr_heaterClearanceOk:
		"No intrusions into the clearance zone. Keep the zone clear — no towels, shelves or other combustibles.",
	bbr_heaterClearanceShieldNote:
		"Side and front distances assume a radiation shield (included in the design). Without a shield, the manufacturer's unshielded distances apply — typically 500 mm for wood-fired heaters.",

	/* ── Domain: Professional Guidance ───────────────────── */
	domain_prof_foundationLabel: "Foundation & floor framing",
	domain_prof_foundationReason:
		"Can normally be done by the builder if ground conditions are straightforward.",
	domain_prof_framingLabel: "Wall framing",
	domain_prof_framingReason: "Carpentry work is well-suited to DIY.",
	domain_prof_systemsLabel: "Heater, ventilation & electrical",
	domain_prof_systemsElectricType: "Licensed electrician",
	domain_prof_systemsElectricReason:
		"Electric heaters must be connected by a licensed electrician.",
	domain_prof_systemsWoodReason:
		"Ventilation, fire-rated boards and stove placement can normally be prepared by the builder.",
	domain_prof_chimneyLabel: "Chimney & fire protection",
	domain_prof_chimneyType: "Chimney sweep / installer",
	domain_prof_chimneyReason:
		"A wood-burning sauna requires fire-safe chimney installation and inspection.",
	domain_prof_drainageLabel: "Drainage & plumbing",
	domain_prof_drainageType: "Plumber",
	domain_prof_drainageReason:
		"A wet area with floor drain and plumbing normally requires a professional.",

	/* ── Domain: Roof ────────────────────────────────────── */
	domain_roof_mono: "Shed roof",
	domain_roof_indoorCeiling: "Flat ceiling under the house roof",
	domain_roof_gable: "Gable roof",
	domain_roof_barrel: "Barrel roof",
	domain_roof_felt_roof: "Roof felt on decking",
	domain_roof_sheet_metal: "Standing seam/sheet metal on decking",
	domain_roof_free_drip: "Free drip edge",

	/* ── Domain: Ventilation ─────────────────────────────── */
	domain_vent_woodAdvice:
		"Wood-fired: the chimney acts as the exhaust while you bathe. Keep the high vent closed during the session and open it afterwards to air out the moisture (drying vent).",
	domain_vent_woodMechWarning:
		"Danger to life: a mechanical exhaust fan should normally not be used with a wood-burning stove. The fan creates negative pressure that can pull deadly carbon monoxide and smoke back down the chimney into the sauna.",
	domain_vent_elNaturalAdvice:
		"Electric, natural draught: the classic layout, required by some heaters such as older Tylö models. Cool air is drawn in under the heater and rises. No fan is needed.",
	domain_vent_elNaturalWarning:
		"Risk of stratified heat (hot ears, cold feet), because the warm air escapes straight out near the ceiling. Build the benches as high towards the ceiling as you can.",
	domain_vent_elMechAdvice:
		"Electric, mechanical downdraft: the layout for the most even heat, recommended by Harvia. The fan extracts air near the floor and pulls the heat down over the bathers' feet. A duct fan in the exhaust pipe is required.",
	domain_vent_singleRoom_woodAdvice:
		"Single room, wood-fired: the fresh-air intake and the drying vent both belong in the sauna room, since there is no separate lounge. The chimney drives the exhaust while you bathe, and the drying vent is opened afterwards.",
	domain_vent_singleRoom_elNaturalAdvice:
		"Single room, electric, natural draught: put the intake below the heater and the exhaust near the ceiling of the same sauna room, since there is no lounge to take the exhaust.",
	domain_vent_singleRoom_elMechAdvice:
		"Single room, electric, mechanical: the exhaust fan must extract from the sauna room, not from a lounge. Size the fan for the whole sauna volume.",
	domain_heater_glassPenaltyWarning:
		"Your windows lose too much heat. Switch to a {kw} kW heater or make the windows smaller.",
	domain_vent_panorama_infiltrationWarning:
		"Panoramic glass: a large glazed area leaks more air than a tight wall. Check the seals and size the vents with margin.",

	/* ── Domain: Foundation warnings ─────────────────────── */
	domain_found_warningGroundHigh:
		"Ground level is too high against the beam line.",
	domain_found_warningPlinthShort:
		"Selected pier length is insufficient for the minimum burial depth.",

	/* ── PDF Export ───────────────────────────────────────── */
	pdf_heading: "Material List & Cost Estimate",
	pdf_3dOverview: "3D Overview",
	pdf_materialList: "Material List",
	pdf_materialListByPhase: "Material List by Purchase Phase",
	pdf_colMaterial: "Material",
	pdf_colDimension: "Dimension",
	pdf_colQuantity: "Quantity",
	pdf_colPrice: "Price (approx)",
	pdf_totalCost: "Estimated total cost (materials)",
	pdf_diyBudget: "Estimated DIY budget",
	pdf_marketPackage: "Market comparison / kit price",
	pdf_buildPlan: "Build Plan & Checklist",
	pdf_cutList: "Cut List",
	pdf_cutColPart: "Part",
	pdf_cutColCount: "Count",
	pdf_cutColLength: "Cut to length",
	pdf_techDetails: "Technical Details",
	pdf_productRecs: "Product Recommendations",
	pdf_generated: "Generated:",
	pdf_openings: "Openings:",
	pdf_roofLabel: "Roof:",
	pdf_heatingLabel: "Heating:",
	pdf_heaterWood: "Wood-burning stove",
	pdf_heaterElectric: "Electric heater",
	pdf_currencyUnit: "SEK",
	pdf_fileSuffix: "_material_list",
	pdf_timeEstimate: "Est. time:",
	pdf_timeUnit: "hours",
	pdf_controlPrefix: "Check",
	pdf_effectiveVolume: "Effective volume:",
	pdf_recommendedPower: "Recommended power:",
	pdf_electricalReq: "Electrical:",
	pdf_chimneyLabel: "Chimney:",
	pdf_footerLine1:
		"Prices and materials are indicative only and may not reflect your local market.",
	pdf_footerLine2:
		"Verify all materials, code requirements, and structural assumptions locally before ordering.",

	/* ── Live running cost (builder) ──────────────────────── */
	runningCost_heatUpLabel: "Heat-up",
	runningCost_glassLabel: "Glass in the hot room",
	runningCost_sessionCostLabel: "Per session",
	runningCost_monthlyCostLabel: "Per month",
	runningCost_minutesShort: "min",
	runningCost_kwShort: "kW",
	runningCost_woodNote: "Rule of thumb: about 15 kg of birch per session",
	runningCost_electricNote: "Estimated electricity cost",
	runningCost_ariaSummary:
		"Heat-up {minutes} minutes, about {cost} per session.",
	runningCost_drawerHeading: "Running cost",
	runningCost_thermalHeading: "With your choices",

	/* ── Spec Pill / Summary Rail ─────────────────────────── */
	specPill_ariaLabel: "Estimated cost {cost}, area {area} m².",
	specPill_ariaLabelAction: "Click for material overview.",
	specPill_est: "Est.",
	specPill_heatUp: "{time} heat-up",
	drawer_eyebrow: "Material overview",
	drawer_closeAriaLabel: "Close material overview",
	rail_projectStatus: "Project status",
	rail_defaultProjectName: "New Sauna",
	rail_hide: "Hide",
	rail_status: "Status",
	rail_panelAriaLabel: "Project overview",
	rail_nextStepLabel: "Next step",
	rail_nextStepFallback: "Review the construction documents",
	rail_planReadyTitle: "Your plan is ready",
	rail_designDetails: "Design details",
	safetyNotice_title: "{count} to review",
	safetyNotice_review: "Open Safety & planning",
	workspace_navAriaLabel: "Builder workspaces",
	rail_planReadyHint: "Drawings, material list, cut list and build steps in one PDF to take to the store.",

	/* ── Design Insights panel ────────────────────────────── */
	insights_buttonLabel: "Insights",
	insights_title: "Design insights",
	insights_subtitle:
		"Everything calculated for your design in one place — search, review, and jump straight to the details.",
	insights_searchPlaceholder: "Search specs …",
	insights_close: "Close",
	insights_empty: "No insights match your search.",
	insights_openWorkspace: "Open {label}",
	insights_statusOk: "Looks good",
	insights_statusWarning: "Review",
	insights_statusDanger: "Action needed",
	insights_groupHeater: "Heater & thermal",
	insights_groupVentilation: "Ventilation",
	insights_groupErgonomics: "Benches & ergonomics",
	insights_groupRoof: "Roof & snow load",
	insights_groupPermit: "Permit & placement",
	insights_groupDrainage: "Drainage",
	insights_groupElectrical: "Electrical & cabling",
	insights_groupCost: "Cost & timeline",
	insights_labelHeaterPower: "Recommended output",
	insights_labelElectrical: "Electrical requirement",
	insights_labelVentFan: "Mechanical fan",
	insights_labelLoyly: "Löyly height",
	insights_labelFloorDrain: "Floor drain",
	insights_labelMaterialCost: "Material cost",
	insights_labelProCost: "Professional work (est.)",
	insights_labelProSteps: "Steps needing a pro",
	insights_weekendValue: "≈ {n} weekends",

	/* ── Workspace labels ─────────────────────────────────── */
	workspace_designLabel: "Design",
	workspace_designTask:
		"Adjust dimensions, openings, and heat strategy directly in the model.",
	workspace_designHint:
		"When the shape feels right, move on to phase-based purchasing.",
	workspace_designNext: "See materials",
	workspace_shoppingLabel: "Materials",
	workspace_shoppingTask:
		"Review the material takeoff phase by phase and catch heavy items early.",
	workspace_shoppingHint:
		"Once the takeoff feels right, review the exact cut lengths.",
	workspace_shoppingNext: "See cut list",
	workspace_cutlistLabel: "Cut list",
	workspace_cutlistTask:
		"Confirm exact cut lengths before you start cutting framing lumber.",
	workspace_cutlistHint:
		"When the cut list looks right, move on to budget and pricing.",
	workspace_cutlistNext: "See budget",
	workspace_pricingLabel: "Budget",
	workspace_pricingTask:
		"See planning cost, price drivers, and recommended purchases.",
	workspace_pricingHint:
		"When the budget works, continue to the build sequence.",
	workspace_pricingNext: "Open build guide",
	workspace_buildLabel: "Build guide",
	workspace_buildTask:
		"Use the build sequence as preliminary planning output before local verification.",
	workspace_buildHint:
		"Finish by reviewing safety assumptions and local verification needs.",
	workspace_buildNext: "Review assumptions",
	workspace_rulesTask:
		"Review safety assumptions, climate-driven sizing, and items that must be verified locally.",
	workspace_rulesHint:
		"Use this as a reference layer, not as a local compliance claim.",

	/* ── Workspace tab badges + completeness ─────────────── */
	workspace_badgeRows_one: "{count} row",
	workspace_badgeRows_other: "{count} rows",
	workspace_badgeRulesOk: "no flags",
	workspace_badgeRulesFlags_one: "{count} flag",
	workspace_badgeRulesFlags_other: "{count} flags",
	workspace_badgeStepsUnit: "steps",
	workspace_badgeStepsDoneOf: "of",
	completeness_label: "Design",
	completeness_complete: "everything set",
	completeness_missingOpenings: "a door or window",
	completeness_missingPostal: "postal code",
	completeness_join: ", ",
	completeness_missingSuffix: "still to add",

	/* ── Quiet milestone feedback ────────────────────────── */
	milestone_rulesAllClear: "All rule checks are clear",
	milestone_completenessNote: "Every design choice is set",

	/* ── Builder summary labels ──────────────────────────── */
	summary_type: "Type",
	summary_size: "Size",
	summary_roof: "Roof",
	summary_area: "Area",
	summary_openings: "Openings",
	summary_heating: "Heating",
	summary_heaterWood: "Wood-burning stove",
	summary_heaterElectric: "Electric heater",
	summary_postalPrefix: "Postal code",

	/* ── Builder misc ────────────────────────────────────── */
	build_startHere: "Start here",
	build_progressLocalNote:
		"Your checked-off steps are saved on this device only. Save the project to your account to keep them across devices.",
	badge_autosaved: "Autosaved",
	badge_autosavedHint: "Saved to your account.",
	toolbar_draftLocal: "Saved on this device",
	toolbar_draftLocalHint: "Kept in this browser. Sign in to save it to your account and open it anywhere.",
	toolbar_undoTitle: "Undo",
	toolbar_undoLabel: "Undo",
	toolbar_redoTitle: "Redo",
	toolbar_redoLabel: "Redo",
	toolbar_shareTitle: "Share your design",
	toolbar_shareLabel: "Share project",
	toolbar_shareCopied: "Link copied!",

	history_buttonLabel: "History",
	history_buttonTitle: "Show change history",
	history_menuLabel: "Recent changes",
	history_empty: "No changes yet.",
	history_currentBadge: "Now",
	history_currentStep: "Current step",
	history_jumpUndo: "Undo back to here",
	history_jumpRedo: "Redo forward to here",
	history_fieldValue: "{label} → {value}",
	history_fieldChanged: "Changed {label}",
	history_toggledOn: "Turned on: {label}",
	history_toggledOff: "Turned off: {label}",
	history_layerShown: "Showing: {label}",
	history_layerHidden: "Hiding: {label}",
	history_deckAdded: "Deck added",
	history_deckRemoved: "Deck removed",
	history_openingAdded: "Added {type} ({wall})",
	history_openingRemoved: "Removed {type} ({wall})",
	history_openingMoved: "Moved {type} ({wall})",
	history_openingResized: "Resized {type} ({wall})",
	history_openingChanged: "Edited {type} ({wall})",
	history_openingsChanged: "Changed openings",
	history_templateApplied: "Template applied",
	history_initialState: "Starting point",
	history_change: "Change",
	history_labelGlassArea: "Glass area",
	history_labelStoneArea: "Stone area",
	history_labelHeaterPlacement: "Heater placement",
	history_labelLayers: "View layers",
	history_labelLocation: "Location setting",
	history_labelRotation: "Orientation",
	history_labelBenchLayout: "Bench layout",
	history_labelBenchAdded: "Bench added",
	history_labelBenchRemoved: "Bench removed",
	history_labelHeaterMoved: "Heater placement",
	domain_interior_variant_straight: "Straight bench",
	domain_interior_variant_twoTier: "Two-tier bench (upper + foot)",
	domain_interior_variant_lShape: "L-shaped bench",
	domain_interior_variant_uShape: "U-shaped bench",
	domain_interior_issue_heaterClearance:
		"Bench is too close to the heater: {actualMm} mm against the required {requiredMm} mm.",
	domain_interior_issue_doorSwing:
		"Bench blocks the door swing ({swingDepthMm} mm in front of the door).",
	domain_interior_issue_doorEntry:
		"Bench stands just inside the door ({entryDepthMm} mm), so you step straight onto it.",
	domain_interior_issue_freeFloor:
		"Too little free floor left: {actualSqm} m² against the required {requiredSqm} m².",
	domain_interior_issue_benchDepth:
		"Bench depth is {actualMm} mm — {requiredMm} mm is needed.",
	domain_interior_issue_benchOverlap: "Two bench runs overlap.",
	domain_interior_issue_loylyHeight:
		"Löyly's law: the ceiling needs to be at least {minCeilingMm} mm, it is {actualCeilingMm} mm.",
	domain_interior_issue_benchStepHeight:
		"The foot bench is {actualMm} mm above the floor. Above {maxMm} mm it needs a step or a third tier, or a lower wall.",
	domain_interior_capacity_summary: "{seats} seats, {lyingSpots} lying places",

	// ── Plan view (issue 132) ──
	plan_view_modeGroup: "View mode",
	plan_view_mode3d: "3D",
	plan_view_modePlan: "Plan",
	plan_view_title: "Plan view — benches, heater and door seen from above",
	plan_view_variantsLabel: "Start from",
	plan_view_zoomGroup: "Zoom",
	plan_view_zoomIn: "Zoom in",
	plan_view_zoomOut: "Zoom out",
	plan_view_zoomReset: "Reset zoom",
	interior_planHint: "Select a bench to edit its dimensions. Drag to move it.",
	interior_edit: "Edit",
	interior_done: "Done",
	interior_properties: "Bench properties",
	interior_back: "Back to design",
	interior_selected: "Selected bench run",
	interior_relaxBench: "Lounge bench",
	interior_length: "Length",
	interior_depth: "Depth",
	interior_start: "From wall start",
	interior_wallGap: "Gap to wall",
	interior_manage: "Design benches",
	interior_add: "Add bench",
	interior_duplicate: "Duplicate bench",
	interior_remove: "Remove bench",
	interior_noSpace: "No free space for this bench. Adjust the layout or dimensions and try again.",
	interior_support: "Support",
	interior_supportAuto: "Automatic",
	interior_supportFloor: "Floor legs",
	interior_supportWall: "Wall support",
	interior_supportFront: "Wall support and front legs",
	interior_supportEnds: "End-wall supports",
	interior_heightGroup: "Move heights together",
	interior_heightGroupHint: "Choose modules in this room. Linked modules keep their height gaps when you change a seat height.",
	interior_linkHeight: "Link heights",
	interior_alignHeight: "Same seat height",
	interior_unlinkHeight: "Unlink this bench",
	interior_front: "Bench front",
	interior_frontOpen: "Open",
	interior_frontSlatted: "Slatted front on legs",
	interior_backrest: "Wall-mounted backrest",
	interior_backrestLength: "Backrest length",
	interior_backrestStart: "Backrest start on bench",
	interior_backrestGap: "Gap above seat",
	interior_backrestBoards: "Backrest board count",
	interior_woodSpecies: "Bench module wood",
	interior_woodFollowRoom: "Follow room wood",
	interior_slatGap: "Seat board gap",
	interior_slatHint: "Seat boards fit evenly across the depth. Buy 28×90 mm stock; with a custom gap, the cut list shows the finished width after ripping. Wood choice also applies to the module’s front and backrest.",
	domain_interior_issue_benchSlats: "Choose a gap between 6 and 25 mm that leaves seat boards at least 45 mm wide.",
	interior_backrestHint: "The backrest follows seat height, with 28×90 mm boards and 18 mm gaps. Requires wall contact and fixings into the wall structure.",
	domain_interior_issue_benchBackrest: "The backrest needs a solid wall, must fit within the bench length and stay below the ceiling. Check its placement and dimensions.",
	domain_interior_issue_benchFront: "A slatted front requires floor legs and enough height for a board. Choose an open front or change the support.",
	interior_supportHint: "Wall supports require structural backing. End supports require contact with both end walls. Span and fixings need a detailed design.",
	domain_interior_issue_benchSupport: "The selected support lacks wall contact or meets an opening/glazed wall. Choose floor legs or change the placement.",
	interior_section: "Section",
	interior_sectionHint: "Drag the height handle up/down or the depth handle sideways. Arrow keys move 10 mm; Shift + arrow moves 100 mm.",
	interior_headroom: "To ceiling",
	interior_sectionHeight: "Change seat height",
	interior_sectionDepth: "Change bench depth",
	interior_empty: "No benches yet. Add a bench to start.",
	interior_clipped: "The heater limits the built length to {lengthMm} mm.",
	interior_seatHeight: "Seat height above floor",
	domain_interior_issue_benchHeadroom: "There is {actualMm} mm between the seat and wall top. Recommended space is {requiredMm} mm.",
	domain_interior_issue_benchHeight: "Seat height must be between {minMm} and {maxMm} mm above the floor.",
	interior_tier: "Level",
	interior_apply: "Apply changes",
	interior_applyHint: "Dimensions adjust to fit the room. Materials, price and cut list update when you apply the change.",
	interior_selectHint: "Select a bench in the plan or in interior editing mode.",
	interior_invalidDimensions: "Enter valid dimensions for the bench run.",
	plan_view_benchUpper: "Upper bench",
	plan_view_benchLower: "Foot bench",
	plan_view_heaterHandle: "Heater — drag to move",
	plan_view_doorHandle: "Door — drag along the wall",
	plan_view_capacityLabel: "Capacity",
	plan_view_capacitySeats_one: "{count} seat",
	plan_view_capacitySeats_other: "{count} seats",
	plan_view_capacityLying_one: "{count} lying place",
	plan_view_capacityLying_other: "{count} lying places",
	plan_view_capacitySummary: "{seats}, {lying}",
	plan_view_issue_openingBounds:
		"The door ends up outside the wall's allowed area.",
	plan_view_issue_openingOverlap: "The door overlaps another opening.",
	plan_view_unavailable: "Plan view is not available for this sauna type.",
	plan_view_pdfTitle: "Plan drawing",
	plan_view_pdfScale: "Scale 1:{denominator}",
	plan_view_pdfLegendBench: "Bench",
	plan_view_pdfLegendHeater: "Heater",
	term_benchLedger: "Bench wall ledger",
	term_benchFrontBoard: "Bench front board",
	term_benchFrame: "Bench frame (rails & legs)",
	term_benchScrews: "Bench screws (frame & seat boards)",
	term_benchSlatsCut: "Bench slats",
	term_benchLedgerCut: "Bench wall ledger",
	term_benchBackRailCut: "Bench rear rail",
	term_benchFrameCut: "Bench frame",
	term_benchLegsCut: "Bench legs",
	term_benchBackrestCut: "Bench backrest",
	term_benchBackrestSpacersCut: "Backrest spacers",
	term_benchSkirtCut: "Bench skirt boards",
	term_benchEndRailsCut: "Bench end rails",
	term_benchJoistsCut: "Bench cross joists",
	term_bench_upper: "upper",
	term_bench_lower: "lower",

	shareDialog_title: "Show off your sauna",
	shareDialog_subtitle:
		"This is what your design looks like to whoever you send the link to.",
	shareDialog_previewAlt: "3D view of {project}, {size}",
	shareDialog_previewCaption: "A preview of your design.",
	shareDialog_previewUnavailable:
		"No preview right now — the 3D view could not be captured. The link still works.",
	shareDialog_summaryHeading: "What the recipient sees",
	shareDialog_projectLabel: "Project",
	shareDialog_sizeLabel: "Outer dimensions",
	shareDialog_priceLabel: "Estimated price",
	shareDialog_linkLabel: "Share link",
	shareDialog_copyError:
		"Could not copy — select the link and copy it manually.",
	shareDialog_nativeShare: "Share",
	shareDialog_downloadImage: "Download image",
	shareDialog_downloadFallbackName: "sauna",
	shareDialog_shareText: "Look what I designed: {project}",
	toolbar_saveLabel: "Save project",
	toolbar_saveTitleAnon: "Sign in to save this draft to your account.",
	toolbar_saveTitleAuthed: "Save to your account.",
	degraded_saveTitle:
		"Saving to your account is unavailable right now. Your design is kept in this browser, and Download PDF still works.",
	degraded_authTitle: "Sign-in is unavailable",
	degraded_saveLabel: "Save project (unavailable)",
	degraded_authBody:
		"The account service couldn't be reached, so sign-in and registration are temporarily off. The builder, guides and calculators all work without an account.",
	degraded_dashboardBody:
		"The account service couldn't be reached, so saved projects can't be shown right now. The builder still works fully.",
	toolbar_savingLabel: "Saving…",
	toolbar_downloadPdf: "Download PDF",
	toolbar_renderPhoto: "Generate render",
	toolbar_rendering: "Rendering…",
	toolbar_menu: "Menu",
	toolbar_more: "More",
	toolbar_shareShort: "Share",
	toolbar_editGroup: "Edit history",
	toolbar_projectLabel: "Project name",
	toolbar_projectPlaceholder: "e.g. Backyard sauna",
	toolbar_buildHeading: "Build your sauna",
	toolbar_typeSquare: "Rectangular sauna",
	toolbar_typeIndoor: "Indoor sauna",
	toolbar_typeBarrel: "Barrel sauna",
	toolbar_typeTwoRoom: "Sauna with lounge",
	store_loginToSave: "Sign in to save",
	store_cannotSave: "Cannot save",
	store_cannotSaveInvalidOpenings: "Cannot save: invalid openings",
	store_saved: "Saved ✓",
	store_createdAndSaved: "Created & saved ✓",
	store_couldNotSave: "Could not save",
	store_autoSaved: "Auto-saved ✓",
	store_autoSaveFailed: "Auto-save failed",
	store_cannotAutoSave: "Cannot auto-save",
	store_cannotAutoSaveInvalidOpenings: "Cannot auto-save: invalid openings",
	modal_close: "Close",
	modal_renderTitle: "Photorealistic render",
	modal_renderAlt: "Rendered sauna",
	modal_renderDownload: "Download",
	camera_side: "Side",
	camera_interior: "Interior",

	/* ── Footer ────────────────────────────────────────────── */
	footer_brand: "Sauna Creator",
	footer_tagline:
		"Free planning tool to design your outdoor sauna with 3D, material lists, and build plans.",
	footer_colTools: "Tools",
	footer_colGuides: "Guides",
	footer_colCompany: "Company",
	footer_privacy: "Privacy",
	footer_about: "About",
	footer_contact: "Contact",
	footer_press: "Press",
	footer_designer: "Sauna Designer",
	footer_costGuide: "What does a sauna cost?",
	footer_buildGuide: "Build a sauna – Guide",
	footer_allGuides: "All guides",
	footer_rentalLink: "bastuhyra.se",
	footer_rentalLabel: "Rent a sauna",
	footer_copyright: "© 2026 Sauna Creator. All rights reserved.",

	/* ── Privacy page ─────────────────────────────────────── */
	privacy_title: "Privacy Policy",
	privacy_author: "Sauna Creator",
	privacy_datePublished: "5 April 2026",
	privacy_intro:
		"Sauna Creator is a free sauna planning tool. We respect your privacy and collect only the minimum data needed to operate the service.",
	privacy_whatWeCollectTitle: "What we collect",
	privacy_whatWeCollectBody:
		"When you use our sauna planner we may process the following categories of data:",
	privacy_dataAuth:
		"Account data — email address and hashed password, stored in Supabase Auth.",
	privacy_dataProjects:
		"Project data — sauna designs you save to your account.",
	privacy_dataAnalytics:
		"Usage analytics — anonymous page-view and interaction data (see below).",
	privacy_analyticsTitle: "Analytics",
	privacy_analyticsBody:
		"We use two privacy-focused analytics services to understand how the tool is used:",
	privacy_analyticsClarityDesc:
		"Session replays and heatmaps. No personal data is collected. Microsoft processes data under their privacy terms.",
	privacy_analyticsSimpleDesc:
		"Cookie-free, privacy-first page-view analytics. No personal data is stored.",
	privacy_cookiesTitle: "Cookies",
	privacy_cookiesBody:
		"Sauna Creator uses only essential cookies required for authentication (Supabase session token). We do not use advertising or third-party tracking cookies.",
	privacy_authTitle: "Authentication",
	privacy_authBody:
		"Account authentication is provided by Supabase. Your password is hashed and never stored in plain text. You can delete your account data at any time through the application settings.",
	privacy_thirdPartyTitle: "Third-party services",
	privacy_thirdPartySupabase:
		"Database, authentication, and storage (EU region).",
	privacy_thirdPartyClarity: "Anonymous session replays and heatmaps.",
	privacy_thirdPartySimple: "Cookie-free analytics, GDPR-compliant by design.",
	privacy_yourRightsTitle: "Your rights",
	privacy_yourRightsBody:
		"You have the right to access, correct, or delete your personal data at any time. You can export your project data as PDF, and request full account deletion through the app or by contacting us.",
	privacy_contactTitle: "Questions",
	privacy_contactBody: "For privacy-related questions, contact us at",

	/* ── About page ───────────────────────────────────────── */
	about_title: "About Sauna Creator",
	about_author: "Sauna Creator",
	about_datePublished: "5 April 2026",
	about_mission:
		"Sauna Creator helps you plan, visualise, and estimate your outdoor sauna project — from the first sketch to a verified material list.",
	about_whatIsTitle: "What is Sauna Creator?",
	about_whatIsBody:
		"Sauna Creator is a free web-based sauna planning tool. It generates a live 3D model of your sauna, calculates structural dimensions, produces a material takeoff with cut lists, and provides baseline safety guidance rooted in Swedish building standards (BBR).",
	about_howItWorksTitle: "How it works",
	about_howStep1: "Choose a sauna shape — rectangular, barrel, or two-room.",
	about_howStep2:
		"Set dimensions, heating type, openings, and site conditions.",
	about_howStep3:
		"Review the auto-generated material list, price estimate, and build plan.",
	about_howStep4: "Export a PDF or share your design with a link.",
	about_whoTitle: "Who is behind this?",
	about_whoBody:
		"Sauna Creator is an independent project built by a small team passionate about making sauna planning accessible. The tool grew out of a personal need: planning a backyard sauna without expensive consulting or CAD software.",
	about_disclaimerTitle: "Important disclaimer",
	about_disclaimerBody:
		"Our sauna planner provides planning output and baseline assumptions — not certified construction documents. Always verify structural, electrical, fire-safety, and permit requirements with local professionals before building.",

	/* ── Contact page ─────────────────────────────────────── */
	contact_title: "Contact Us",
	notFound_metaTitle: "Page not found | Sauna Creator",
	notFound_title: "Page not found",
	notFound_body: "This address does not lead to a page here. It may have moved, or a character may be wrong.",
	notFound_home: "Go to the homepage",
	notFound_guides: "Browse all guides",
	contact_author: "Sauna Creator",
	contact_datePublished: "5 April 2026",
	contact_intro:
		"Have a question, found a bug, or want to suggest a feature? We would love to hear from you.",
	contact_emailLabel: "Email us directly",
	contact_feedbackTitle: "Feature requests & bugs",
	contact_feedbackBody:
		"We actively use feedback to improve the tool. If you spot an issue with material calculations, building code references, or anything else — let us know and we will investigate.",
	contact_responseTitle: "Response time",
	contact_responseBody:
		'We aim to respond within 2–3 business days. For urgent issues, include "urgent" in the subject line.',

	/* ── Landing FAQ (GEO) ────────────────────────────────── */
	landing_faqTitle: "Common questions about building an outdoor sauna",
	landing_faq1Q: "How much does it cost to build your own outdoor sauna?",
	landing_faq1A:
		"Mostly it depends on size, heater and glass. We priced the two layouts the planner opens with. The one-room sauna, {oneRoomSize} m with aspen panelling and a wood-fired stove, comes to about {oneRoomPrice} in materials. {preset}, a {lakesideSize} m two-room sauna with a lounge and a panoramic window, comes to about {lakesidePrice}. In the one-room sauna, the stove, insulated chimney and heat shields are {heaterBlock} of that. Spruce panelling, fewer windows or a smaller room bring it down. Our sauna cost guide goes through the list line by line.",
	landing_faq2Q: "Do I need a building permit for a sauna in Sweden?",
	landing_faq2A:
		"In most cases, no. Since 1 December 2025 you can build a permit-exempt outbuilding of up to 30 m² inside zoned (detaljplan) areas (50 m² outside) without a permit or notification, provided it is placed at least 4.5 m from the property boundary (or with your neighbour's written consent) and the ridge height stays at 4.5 m or below. The cap per plot is 45 m² combined inside zoned areas (65 m² outside). A wood-fired heater still requires a separate fire notification (eldstadsanmälan) to your municipality, and shoreline protection (strandskydd) rules continue to apply.",
	landing_faq3Q:
		"Wood-fired or electric heater — which is better for an outdoor sauna?",
	landing_faq3A:
		"A wood-fired stove delivers a traditional löyly experience with soft, radiant heat and works without electricity, but requires a certified chimney installation and annual chimney sweep inspection. An electric heater is simpler to install and regulate — just set the thermostat — but needs a dedicated 3-phase circuit (400 V) for heaters above 4.5 kW. Choose wood for authenticity, electric for convenience.",
	landing_faq4Q: "What wood should I use for an outdoor sauna?",
	landing_faq4A:
		"Use pressure-treated spruce (tryckimpregnerad gran) for the structural frame and floor joists since they are in contact with ground moisture. For interior panelling and benches, choose untreated aspen (asp) or alder (al) — both stay cool to the touch and resist warping at high temperatures. The exterior façade works well in heat-treated pine (thermoträ) or larch, which handles weather without chemical treatment.",
	landing_faq5Q: "How long does it take to build a sauna yourself?",
	landing_faq5A:
		"A handy builder can finish a 4-person rectangular outdoor sauna in 4–8 weekends (roughly 60–120 hours). The foundation and framing take about two weekends, roofing and exterior cladding another two, and insulation, interior panelling, benches, and heater installation fill the rest. Professional steps — electrical connection and chimney certification — add 1–2 scheduled appointments.",

	/* ── Landing: Popular Guides ───────────────────────────── */
	landing_guidesTitle: "Popular guides",
	landing_guidesIntro:
		"Everything you need to know before starting — planning, permits, materials, ventilation, and heater sizing.",
	landing_guideBuildTitle: "Build a sauna – complete guide",
	landing_guideBuildBlurb: "Step by step from foundation to final inspection",
	landing_guideCostTitle: "What does a sauna cost?",
	landing_guideCostBlurb: "Line-item breakdown from two real layouts",
	landing_guidePermitTitle: "Sauna permits",
	landing_guidePermitBlurb:
		"Zoning, inspections, fire clearances, and local checks",
	landing_guidePanelTitle: "Sauna panel",
	landing_guidePanelBlurb: "Aspen, alder or spruce? Price and properties",
	landing_guideVentTitle: "Ventilation",
	landing_guideVentBlurb: "Fresh air, exhaust placement, and drying airflow",
	landing_guideHeaterTitle: "Heater sizing",
	landing_guideHeaterBlurb: "kW estimate, glass penalty, and warm-up comfort",
	landing_guidesAll: "See all guides →",

	/* ── SEO ──────────────────────────────────────────────── */
	seo_title: "Sauna Creator – free 3D outdoor sauna planner",
	seo_description:
		"Sketch an outdoor sauna with live 3D, structural calculations, and material takeoffs you can verify locally.",

	/* ── Common / reusable ───────────────────────────────── */
	common_loading: "Loading…",
	common_error: "Something went wrong.",
	common_retry: "Try again",
	errorBoundary_title: "Something went wrong",
	errorBoundary_body:
		"We've been notified and are looking into it. Try reloading the page — if the issue persists, get in touch.",
	errorBoundary_action: "Reload",
	common_save: "Save",
	common_cancel: "Cancel",
	common_close: "Close",
	common_yes: "Yes",
	common_no: "No",
	common_or: "or",

	/* ── Builder toasts ───────────────────────────────────── */
	toast_doorAdded: "Door added",
	toast_windowAdded: "Window added",
	toast_doorRemoved: "Door removed",
	toast_windowRemoved: "Window removed",
	toast_heaterChanged: "Heater changed to {type}",

	/* ── Term: Material & Part Names (i18n for builder tables) ── */
	term_concretePier: "Concrete pier",
	term_treatedBearer: "Treated bearer (pressure-treated)",
	term_floorRoofJoists: "Floor & roof joists",
	term_wallStudsSillPlate: "Wall studs, sill plate, top plate",
	term_wallStudsDivision: "Wall studs (exterior + divider)",
	term_mineralWoolInsulation: "Mineral wool insulation",
	term_saunaFoil: "Sauna vapour barrier foil",
	term_furringStrips: "Furring strips / battens",
	term_saunaPanelSpruce: "Sauna panel (e.g. spruce/aspen)",
	term_saunaPanelAspen: "Sauna panel (sauna room)",
	term_interiorPanelLounge: "Interior panel (lounge)",
	term_benchTimber: "Bench timber (knot-free)",
	term_moistureResistantFloor: "Moisture-resistant flooring",
	term_exteriorCladding: "Exterior cladding (tongue & groove)",
	term_exteriorFurring: "Exterior nailing battens (horizontal)",
	term_exteriorBattenSpacer: "Exterior spacer battens (air gap)",
	term_windBarrierBoard: "Wind barrier (board/membrane)",
	term_dim_windBarrier: "Wind barrier board / vapour-open membrane",
	term_cornerTrim: "Corner trim boards",
	term_baseTrim: "Base trim / drip edge",
	term_fasciaBoard: "Fascia boards",
	term_soffitBoard: "Soffit / roof underlay",
	term_doorAndAccessories: "Door & accessories",
	term_doorsAndAccessories: "Doors & accessories",
	term_subfloor: "Subfloor / sheathing",
	term_subfloorLedger: "Subfloor ledger batten",
	term_dim_asphaltBoard: "12 mm bitumen-impregnated fibreboard",
	term_staveTimber: "Stave timber (heat-treated)",
	term_endCaps: "End caps (glue-laminated)",
	term_steelBands: "Steel bands",
	term_cradles: "Cradles / supports",
	term_floorBoards: "Floor boards (interior)",
	term_benchTimberBarrel: "Bench timber (knot-free)",
	term_saunaDoor: "Sauna door (glass)",
	term_heaterUnit: "Heater unit + flue pipe",
	term_vents: "Vents",
	term_deckBoard: "Deck board",
	term_joist: "Joist",
	term_bearer: "Bearer",
	term_deckFascia: "Deck fascia board",
	term_nogging: "Nogging / blocking",
	term_deckScrew: "Deck screw",
	term_bearersCut: "Bearers",
	term_floorJoistsCut: "Floor joists",
	term_roofJoistsCut: "Roof joists",
	term_roofJoistsGlulam: "Roof joists (glulam)",
	term_roofJoistsGlulamCut: "Roof joists (glulam, sized by a structural engineer)",
	term_dim_glulamPlaceholder:
		"Sized by a structural engineer (drawn as 45×220, priced as 56×225 glulam)",
	term_sillPlateCut: "Sill plate / top plate",
	term_sillBottomCut: "Sill plate (sides)",
	term_topPlateCut: "Top plate",
	term_wallStudsFrontCut: "Wall studs (front)",
	term_wallStudsBackCut: "Wall studs (back)",
	term_wallStudsSidesCut: "Wall studs (sides)",
	term_fasciaBoardsCut: "Fascia boards",
	term_fasciaFrontBackCut: "Fascia boards (front/back)",
	term_exteriorCladdingCut: "Exterior cladding",
	term_cornerTrimCut: "Corner trim",
	term_baseTrimCut: "Base trim",
	term_soffitBoardCut: "Soffit / roof underlay",
	term_stavesCut: "Staves",
	term_endCapsCut: "End cap panels",
	term_steelBandsCut: "Steel bands",
	term_cradlesCut: "Cradles",
	term_floorBoardsCut: "Floor boards",
	term_dividerSillPlateCut: "Divider wall sill/top plate",
	term_dividerStudsCut: "Divider wall studs",
	term_subfloorCut: "Subfloor / sheathing",
	term_fasciaEndCut: "Fascia / end trim",
	term_deckBoardsCut: "Deck boards",
	term_joistsCut: "Joists",
	term_bearersDeckCut: "Bearers",
	term_deckFasciaCut: "Fascia boards",
	term_noggingsCut: "Noggings / blocking",
	term_purchase: "purchase",
	term_waste: "waste",

	/* ── Intl material coverage (opening trim, panorama supports, framing) ── */
	term_openingHeader: "Opening header",
	term_doorCasing: "Door casing",
	term_panoramaHeader: "Panorama header",
	term_windowApron: "Window apron",
	term_windowCasing: "Window casing",
	term_panoramaUpperSupport: "Panorama upper support",
	term_panoramaLowerSupport: "Panorama lower support",
	term_panoramaSupport: "Panorama support",
	term_side_right: "right",
	term_side_left: "left",
	term_wall_outer: "outer wall",
	term_partition_junction: "partition junction",
	term_wall_long: "long wall",
	term_dim_saunaFixture: "IP-rated / heat-resistant",
	term_dim_singleLayerMembrane: "1-layer waterproofing",
	term_dim_lacqueredSheet: "Lacquered sheet metal",
	term_dim_prefoldedSheet: "Pre-folded sheet metal",
	term_cut_runningWallAngle:
		"Running along wall/roof angle, approx. {count} lm",

	/* ── Units ──────────────────────────────────────────────── */
	term_unit_pcs: "pcs",
	term_unit_lm: "lm",
	term_unit_set: "set",

	/* ── Quantity phrases ──────────────────────────────────── */
	term_qty_wall: "Wall",
	term_qty_roofFloor: "Roof & Floor",
	term_qty_roof: "Roof",
	term_qty_floor: "Floor",
	term_qty_saunaSection: "sauna section",
	term_qty_loungeSection: "lounge section",
	term_qty_incl: "Incl.",
	term_qty_aluTape: "alu tape",
	term_qty_saunaDoor: "Sauna door",
	term_qty_exteriorDoor: "Exterior door",
	term_qty_interiorDoor: "Interior door",
	term_qty_vents: "Vents",
	term_qty_heaterUnit: "Heater unit",
	term_qty_screws: "Screws",
	term_qty_window: "Window",
	term_qty_woodRecommended: "wood-burning recommended",
	term_qty_supplyExhaust: "supply + exhaust",
	term_qty_approx: "approx.",

	/* ── Dimension phrases ─────────────────────────────────── */
	term_dim_cover: "cover",
	term_dim_board: "board",
	term_indoorOuterPanel: "Outer panelling, free-standing walls",
	term_indoorFibreboard: "Wood-fibre board against existing wall",
	term_indoorOuterPanelCut: "Outer panelling (free-standing walls)",
	term_dim_airGap: "air gap",
	term_dim_structuralTimber: "structural timber (C24)",
	term_ceilingPanelCut: "Ceiling panel",
	term_dim_roofUnderlay: "Roofing underlay",
	term_saunaLightFixture: "Sauna light fixture",
	term_dim_ipRatedHeatResistant: "IP-rated, heat-resistant",
	term_wiringAccessories: "Wiring accessories",
	term_siliconeCable: "Silicone cable",
	term_dim_clipsBoxesConduit: "Clips, junction boxes and conduit",
	term_dim_hotZoneWiring: "Hot-zone wiring",

	/* ── Cut-length construction phrases ───────────────────── */
	term_cut_perRoofSlope: "per roof slope",
	term_cut_overhang: "overhang",
	term_cut_ceilingJoistNoOverhang: "ceiling joist, no overhang",
	term_cut_overlap: "overlap",
	term_cut_cutOnSite: "Cut on site",
	term_cut_cutAt: "Cut at",
	term_cut_panelCutOnSite: "Panel-cut on site to",
	term_cut_gables: "gables",
	term_cut_gableCut: "gable cut",
	term_cut_gablePeak: "gable peak",
	term_cut_gableSidesFlat: "gable sides flat",
	term_cut_angleCut: "cut",
	term_cut_ascendingLength: "ascending length from back to front",
	term_cut_ridgeRoof: "ridge roof",
	term_cut_shedRoof: "shed roof",
	term_cut_sidesTapered: "sides tapered and front built up for",
	term_cut_matchRoofPitch: "to match roof pitch",
	term_cut_circularSawn: "Circular-sawn",
	term_cut_profileCut: "Profile-cut",
	term_cut_corners: "corners",
	term_cut_door: "door",
	term_cut_around: "around",
	term_cut_follows: "follows",
	term_cut_and: "and",
	term_cut_topPlate: "top plate",
	term_cut_sillPlate: "sill plate",

	/* ── Electrical BOM terms ──────────────────────────────── */
	term_el_ceilingSpots: "Ceiling spots (recessed)",
	term_el_wallLightHigh: "Wall light (high)",
	term_el_heaterConnection: "Heater connection",
	term_el_ledStripLow: "LED strip (low, under bench)",
	term_el_cable: "Cable",
	term_el_siliconeCable:
		"Heat-resistant silicone cable (SiHF 3×1.5 mm², 180°C)",
	term_el_standardCable: "Standard cable (EKK 3×1.5 mm²)",
	term_el_hotZoneWarning:
		"Above 500 mm, standard conduit and PVC cable (such as EKK/FKX) cannot be used: they melt at around 70 °C.",
	term_el_transformerWarning:
		"Keep the transformer outside the sauna, unless it is mounted at floor level (below 500 mm).",

	/* ── Term: Additional material names (roof, foundation, systems) ── */
	term_postShoe: "Post shoe / bracket",
	term_groundFabricMat: "Ground fabric",
	term_drainageGravel: "Drainage gravel / macadam",
	term_aluTapeMat: "Alu tape / seam tape",
	term_stainlessScrews: "Stainless steel screws / hardware kit",
	term_surfaceTreatment: "Surface treatment / paint / oil",
	term_controlPanel: "Control panel",
	term_roofDecking: "Roof decking (tongue & groove)",
	term_roofingUnderlay: "Roofing underlay",
	term_eaveFlashing: "Eave flashing",
	term_vergeFlashing: "Verge flashing",
	term_ridgeCapping: "Ridge capping",
	term_roofingFelt: "Roofing felt (top layer)",
	term_sheetMetalRoofing: "Sheet metal roofing",
	term_standingSeamRoofing: "Standing-seam sheet metal roofing",
	term_partitionBlocking: "Blocking for partition wall",

	/* ── Dimension phrases (additional) ────────────────────── */
	term_dim_foundationBracket: "Foundation bracket",
	term_dim_geotextile: "Geotextile",
	term_dim_baseLayer: "Base layer",
	term_dim_frameInteriorFastening: "Frame and interior fastening",
	term_dim_facadeExposedTimber: "Façade and exposed timber surfaces",
	term_dim_bandsHardwareFastening: "Bands, hardware and fastening",
	term_dim_externalIntegrated: "External/integrated",

	/* ── Quantity unit (additional) ─────────────────────────── */
	term_unit_pack: "pack",

	/* ── Domain: Comparison Advisor (reason keys) ────────────── */
	domain_comp_reasonPermitOk:
		"Has the least permit friction and sits in a safer regulatory position.",
	domain_comp_reasonPermitLower:
		"Has lower regulatory risk than the alternatives, even though checks are still needed.",
	domain_comp_reasonBestEconomy:
		"Gives the best total economy when material cost and running costs are weighed together.",
	domain_comp_reasonEasiestBuild:
		"Is easier to build with a shorter planned effort and fewer heavy specialist steps.",
	domain_comp_reasonPractical:
		"Is more practical to get home and complete without unnecessary logistics friction.",
	domain_comp_reasonNoChimney:
		"Avoids the fireplace notification, chimney and extra fire-protection step that the wood-burning alternatives require.",
	domain_comp_reasonNoPermitStop:
		"Keeps the project moving faster by reducing the risk of late permit or notification stops.",
	domain_comp_reasonBestBalance:
		"Lands best in balance between budget, buildability and risk right now.",

	/* ── Domain: Comparison Advisor (caution keys) ───────────── */
	domain_comp_cautionPermitFull:
		"Full planning permission required before construction starts.",
	domain_comp_cautionPermitNotice: "Building notice needs to be secured early.",
	domain_comp_cautionFireplace:
		"Wood-burning solution requires a fireplace notification.",
	domain_comp_cautionFireDistance:
		"Plan at least {distance} m fire-safety distance to the nearest building.",
	domain_comp_cautionShower:
		"Shower mode requires drainage and plumbing planning.",

	/* ── Domain: Comparison Advisor (heater label) ───────────── */
	domain_comp_heaterWood: "Wood-burning stove",
	domain_comp_heaterElectric: "Electric heater",
	domain_comp_permitFallback: "Check required",

	/* ── Domain: Cable Spec (key-based labels) ───────────────── */
	domain_cable_fuse_1x10A: "1×10A single-phase",
	domain_cable_fuseLightingGroup: "Separate circuit for lighting and controls, as specified by the electrician",
	domain_cable_typeFeed: "{size} installation cable (EKLK or the local equivalent)",
	bbr_approxLength: "about {m} m",
	bbr_hotZoneCableRule: "Above {mm} mm is the hot zone: use {silicone}, not {standard}.",
	domain_cable_fuse_1x16A: "1×16A single-phase",
	domain_cable_fuse_3x10A: "3×10A three-phase",
	domain_cable_fuse_3x16A: "3×16A three-phase",
	domain_cable_fuse_3x20A: "3×20A three-phase",
	domain_cable_fuse_3x25A: "3×25A three-phase",
	domain_cable_rcd:
		"RCD 30 mA — mandatory for all sauna electrical installations.",
	domain_cable_rcdObligatory: "RCD 30 mA: MANDATORY",
	domain_cable_typeWoodBell:
		"Zone-controlled lighting circuit per SEK, no power feed to wood-burning stove.",
	domain_cable_warningWood:
		"A wood-burning stove does not need a power feed, but lighting in the hot zone must always use SiHF cable.",
	domain_cable_warningLargeUnit:
		"Large units normally require three-phase supply and should be verified against the property’s main fuse.",

	/* ── Term: Missing material patterns from engines ────────── */
	term_heaterStones: "Heater Stones",
	term_ceilingPanelSauna: "Ceiling Panel (Sauna Section)",
	term_ceilingPanel: "Ceiling Panel",
	term_electricHeater: "Electric Heater",
	term_window: "Window",
	term_ceilingPanelLounge: "Ceiling Panel (Lounge Section)",
	term_interiorDoor: "Interior Door",
	term_panoramaGlass: "Panorama glass",
	term_heatShield: "Heat shield",
	term_ceilingBattens: "Ceiling Battens / Furring",
	term_crownMoulding: "Crown Moulding / Trim Strip",
	term_exteriorDoor: "Exterior Door",

	/* ── Term: Missing dimension phrases ────────────────────── */
	term_dim_woodBurningStove: "Wood-burning stove",
	term_fireBoardCalciumSilicate: "Calcium silicate fire board",
	term_feedTunnelSeal: "Feed tunnel fire seal",
	term_dim_fireBoardBothFaces:
		"1220×1000×50 mm, both faces of the wall opening",
	term_dim_feedTunnelSeal: "Non-combustible A1 stone wool around the tunnel",
	term_dim_hardenedGlassDoor: "Tempered glass / wooden knob",
	term_dim_midSegment: "Mid-segment",
	term_dim_interiorGlassDoor: "Interior glass door",
	term_dim_insulatedGlassLounge: "Insulated glass, lounge",
	term_dim_insulatedGlass: "Insulated glass",
	term_dim_temperedInsulatedGlass: "Tempered insulated glass unit (6 mm + 6 mm)",
	term_dim_heaterShieldPlate: "Heater shield plate",
	term_dim_entranceDoor: "Entrance / exterior door",
	term_dim_aluminium: "Aluminium",
};
